/**
 * Context-aware historical popup for an option contract/strike.
 * - Primary chart depends on click source (bid / ask / volume / trades / oi).
 * - Trades chart (premium flow) with date range.
 * - First-, second-, and third-order Greeks; historical IV.
 * - Bought vs sold % (from bid/ask size); open vs close % (from volume and OI change).
 * - Tape-style bid vs ask from trades + session NBBO (see tape_summary / contract-tape-by-day).
 * - Unusual flow: dates where (value / average) > 3.
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl } from '../config/api';
import { formatDate } from '../utils/dateUtils';
import {
  Area,
  AreaChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { isThetaDataAvailable } from '../services/thetaDataService';
import { isFuturesModeEnabled, getOptionsUnderlying } from '../utils/unifiedSymbolMapping';
import TrackButton from './TrackButton';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { readChartTheme, useChartTheme } from '../theme/chartTheme';
import type { ColorScheme } from '../utils/colorScheme';
import type { NormalizedFlowLeg, FlowPatternRight } from '../types/flowPatternLeg';
import { normalizeHistoricalTradeRow } from '../utils/flowPatternRecognition';
import { multiLegInfo } from '../utils/opraConditions';
import { dedupePrints } from '../utils/flowLegGrouping';
import { getMmecAuthHeaderFields } from '../utils/authUtils';
import FlowPatternRecognitionPanel from './flow/FlowPatternRecognitionPanel';
import SteppedOrderFlowChart from './SteppedOrderFlowChart';
import {
  counterFlowCandidates,
  eodPositionEffects,
  formatPositionEffect,
  type EodOiRow,
} from '../utils/eodPositionEffect';
import { liveOiReading, openInterestLabel } from '../utils/liveSessionOi';
import { buildPublishedOiUrl, parsePublishedOi } from '../utils/publishedOpenInterest';
import type { PublishedOpenInterest } from '../utils/publishedOpenInterest';
import { useContractHistoricalEod } from '../hooks/useContractHistoricalEod';
import { sessionNetPosition, sessionOutcome } from '../utils/sessionNetPosition';

/** Coerce chart / API values so Recharts never sees NaN/Infinity (avoids invalid SVG polyline points). */
function chartFiniteNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

const HEAVY_TICKERS = new Set(['NVDA', 'SPX', 'SPXW', 'SPY', 'QQQ']);
function isHeavyTicker(sym: string | undefined): boolean {
  return HEAVY_TICKERS.has((sym || '').trim().toUpperCase());
}
const DEFAULT_INTRADAY_CHART_POINTS = 2500;

function downsampleUniform<T>(arr: T[], maxPoints: number): T[] {
  if (arr.length <= maxPoints) return arr;
  const step = Math.ceil(arr.length / maxPoints);
  return arr.filter((_, i) => i % step === 0);
}

type IntradayRowLite = {
  time?: string;
  datetime?: string;
};

/** Parse sort key for intraday rows (session `time` is HH:MM:SS and is not unique per print). */
function intradayRowTimeMs(row: IntradayRowLite, dayYmd: string): number {
  const dt = row.datetime;
  if (typeof dt === 'string' && dt.length >= 8) {
    const p = Date.parse(dt);
    if (Number.isFinite(p)) return p;
  }
  const day = (dayYmd || '').slice(0, 10);
  const t = (row.time || '12:00:00').trim();
  const hm = t.length >= 8 ? t.slice(0, 8) : '12:00:00';
  const iso = `${day}T${hm}`;
  const u = Date.parse(iso);
  return Number.isFinite(u) ? u : 0;
}

/**
 * +1 buyer-initiated (lift), −1 seller-initiated (hit), 0 mid/unknown.
 * Matches backend execution_side when present; else tape_side vs NBBO.
 */
function intradayAggressorSign(row: {
  execution_side?: string;
  tape_side?: string;
}): -1 | 0 | 1 {
  const ex = String(row.execution_side ?? '').toLowerCase();
  if (ex === 'buy') return 1;
  if (ex === 'sell') return -1;
  if (ex === 'mid') return 0;
  const ts = String(row.tape_side ?? '');
  if (ts === 'at_ask') return 1;
  if (ts === 'at_bid') return -1;
  return 0;
}

type IntradayAggRow = IntradayRowLite & {
  size?: number;
  execution_side?: string;
  tape_side?: string;
};

function intradayFlowQuadrantDelta(right: 'C' | 'P', executionSide: string | undefined, tapeSide?: string): {
  bc: number;
  sc: number;
  bp: number;
  sp: number;
} {
  let ex = String(executionSide ?? '').toLowerCase();
  // Fall back to tape_side when execution_side is not set
  if (!ex || ex === 'mid') {
    const ts = String(tapeSide ?? '').toLowerCase();
    if (ts === 'at_ask') ex = 'buy';
    else if (ts === 'at_bid') ex = 'sell';
  }
  const z = { bc: 0, sc: 0, bp: 0, sp: 0 };
  if (ex === 'mid' || ex === '') return z;
  if (right === 'C') {
    if (ex === 'buy') z.bc = 1;
    else if (ex === 'sell') z.sc = 1;
  } else {
    if (ex === 'buy') z.bp = 1;
    else if (ex === 'sell') z.sp = 1;
  }
  return z;
}

/** Merge call + put prints, chronological (for cumulative session charts). */
function mergeIntradayTaggedForTimeSort(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ row: IntradayAggRow; right: 'C' | 'P'; seq: number }> {
  const tagged = [
    ...callRows.map((row, i) => ({ row, right: 'C' as const, seq: i })),
    ...putRows.map((row, i) => ({ row, right: 'P' as const, seq: i })),
  ];
  tagged.sort((a, b) => {
    const da = intradayRowTimeMs(a.row, dayYmd);
    const db = intradayRowTimeMs(b.row, dayYmd);
    if (da !== db) return da - db;
    if (a.right !== b.right) return a.right.localeCompare(b.right);
    return a.seq - b.seq;
  });
  return tagged;
}

function buildIntradayFlowQuadrantCumulative(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ xKey: string; tickLabel: string; boughtCall: number; soldCall: number; boughtPut: number; soldPut: number }> {
  const merged = mergeIntradayTaggedForTimeSort(callRows, putRows, dayYmd);
  let bc = 0;
  let sc = 0;
  let bp = 0;
  let sp = 0;
  const out: Array<{ xKey: string; tickLabel: string; boughtCall: number; soldCall: number; boughtPut: number; soldPut: number }> = [];
  merged.forEach((t, i) => {
    const sz = Math.abs(chartFiniteNumber(t.row.size, 0));
    const d = intradayFlowQuadrantDelta(t.right, t.row.execution_side, t.row.tape_side);
    bc += sz * d.bc;
    sc += sz * d.sc;
    bp += sz * d.bp;
    sp += sz * d.sp;
    const tickLabel = (t.row.time && String(t.row.time).trim()) || String(i);
    out.push({ xKey: String(i), tickLabel, boughtCall: bc, soldCall: sc, boughtPut: bp, soldPut: sp });
  });
  return out;
}

function buildIntradayBuySellCumulative(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ xKey: string; tickLabel: string; buyVol: number; sellVol: number }> {
  const merged = mergeIntradayTaggedForTimeSort(callRows, putRows, dayYmd);
  let buyVol = 0;
  let sellVol = 0;
  return merged.map((t, i) => {
    const sz = chartFiniteNumber(t.row.size, 0);
    let ex = String(t.row.execution_side ?? '').toLowerCase();
    // Fall back to tape_side when execution_side is not set
    if (!ex || ex === 'mid') {
      const ts = String(t.row.tape_side ?? '').toLowerCase();
      if (ts === 'at_ask') ex = 'buy';
      else if (ts === 'at_bid') ex = 'sell';
    }
    if (ex === 'buy') buyVol += Math.abs(sz);
    else if (ex === 'sell') sellVol += Math.abs(sz);
    const tickLabel = (t.row.time && String(t.row.time).trim()) || String(i);
    return { xKey: String(i), tickLabel, buyVol, sellVol };
  });
}

function buildIntradayTapeLocationCumulative(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ xKey: string; tickLabel: string; atAsk: number; atBid: number; inside: number }> {
  const merged = mergeIntradayTaggedForTimeSort(callRows, putRows, dayYmd);
  let atAsk = 0;
  let atBid = 0;
  let inside = 0;
  return merged.map((t, i) => {
    const sz = chartFiniteNumber(t.row.size, 0);
    const ts = String(t.row.tape_side ?? '');
    if (ts === 'at_ask') atAsk += Math.abs(sz);
    else if (ts === 'at_bid') atBid += Math.abs(sz);
    else inside += Math.abs(sz);
    const tickLabel = (t.row.time && String(t.row.time).trim()) || String(i);
    return { xKey: String(i), tickLabel, atAsk, atBid, inside };
  });
}

/** Buy / sell / mid for tape summary (matches Side column: execution_side ?? side). */
function flowTapeAggressionSide(t: { execution_side?: string; side?: string }): 'buy' | 'sell' | 'mid' {
  const s = String(t.execution_side ?? t.side ?? '').toLowerCase();
  if (s === 'buy') return 'buy';
  if (s === 'sell') return 'sell';
  return 'mid';
}

/** Format USD premium for tape summary (millions). */
function formatPremiumMillions(usd: number): string {
  if (!Number.isFinite(usd)) return '—';
  const m = usd / 1e6;
  const abs = Math.abs(m);
  const digits = abs >= 100 ? 1 : abs >= 10 ? 2 : 2;
  const rounded = m.toFixed(digits);
  return `${rounded}M`;
}

/** Calendar day for Flow tape table from API timestamp (ET-style ISO or numeric date). */
function flowTapeRowDate(t: { timestamp?: string; time?: string }): string {
  const ts = t.timestamp;
  if (typeof ts === 'string' && ts.length >= 8) {
    if (ts.length >= 10 && ts[4] === '-' && ts[7] === '-') return ts.slice(0, 10);
    const head = ts.includes('T') ? ts.split('T')[0] : '';
    if (head && /^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
    const digits = ts.replace(/\D/g, '');
    if (digits.length >= 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  return '—';
}

export type { HistoricalSource, ContractHistoricalParams } from '../types/contractHistorical';
import type { ContractHistoricalParams } from '../types/contractHistorical';

interface ContractHistoricalModalProps {
  open: boolean;
  onClose: () => void;
  params: ContractHistoricalParams | null;
  isDarkMode?: boolean;
  /** Full-width bottom sheet on narrow viewports (simple mode). */
  compactChrome?: boolean;
}



function resolveFuturesUnderlying(sym: string): string {
  if (!isFuturesModeEnabled()) return sym;
  const etf = getOptionsUnderlying(sym);
  return etf !== sym ? etf : sym;
}


const RELATIVE_UNUSUAL_THRESHOLD = 3;

function smallChart(
  title: string,
  data: { date: string; value: number }[],
  colors: ColorScheme,
  lineColor: string,
  height = 220,
  isDark = false,
) {
  if (!data.length) return null;
  const ct = readChartTheme();
  const axis = ct.border;
  const grid = ct.grid;
  const tip: React.CSSProperties = {
    fontSize: 12,
    borderRadius: 8,
    background: 'var(--elev)',
    border: '1px solid var(--border)',
    color: 'var(--txt)',
    fontFamily: 'var(--font-ui)',
    boxShadow: 'var(--lift-2)',
  };
  return (
    <div
      className="mb-6 rounded-2xl p-4 border"
      style={{
        backgroundColor: 'var(--elev)',
        borderColor: 'var(--hair)',
        borderRadius: 10,
      }}
    >
      <h4 className="text-sm font-semibold mb-3" style={{ color: colors.text }}>
        {title}
      </h4>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="date" stroke={axis} axisLine={{ stroke: axis }} tickLine={false} tick={{ fill: ct.text, fontSize: 11, fontFamily: 'var(--font-mono)' }} />
          <YAxis stroke={axis} axisLine={{ stroke: axis }} tickLine={false} tick={{ fill: ct.text, fontSize: 11, fontFamily: 'var(--font-mono)' }} width={48} />
          <Tooltip
            contentStyle={tip}
            labelStyle={{ color: 'var(--txt)', fontWeight: 500 }}
            itemStyle={{ color: 'var(--txt-2)' }}
            formatter={(v: number) => [typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(4) : v, title]}
          />
          <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={2.25} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Every request this modal makes, carrying who is making it.
 *
 * These calls were previously anonymous, so the backend's per-user limits could not apply
 * to them -- the gate's first act is to admit anything without a token, and this modal is
 * the heaviest thing on the platform: one open fans out into tens of upstream calls. With
 * the token attached the server can hold one person to one request at a time instead of
 * letting a single client take the shared provider budget.
 */
const modalFetch = (input: string, init?: RequestInit): Promise<Response> =>
  fetch(input, {
    ...init,
    headers: { ...getMmecAuthHeaderFields(), ...(init?.headers || {}) },
  });


const ContractHistoricalModal: React.FC<ContractHistoricalModalProps> = ({
  open,
  onClose,
  params,
  isDarkMode = false,
  compactChrome = false,
}) => {
  const { language } = useLanguage();
  const { colors: uiColors } = useTheme();
  const chartT = useChartTheme();
  const [activeTab, setActiveTab] = useState<'eod' | 'intraday' | 'flow'>('eod');

  // The figure that settles the last completed session. OPRA stamps open interest once,
  // around 06:30 ET, reporting positions after the PREVIOUS session closed — and it is in
  // neither the EOD rows (which stop at the completed session and carry the one before it)
  // nor `todaySnapshot`, a field no backend actually populates. Without this one request the
  // table says "Pending" about a question that was answered this morning.
  const [publishedOi, setPublishedOi] = useState<PublishedOpenInterest | null>(null);

  // One pipeline, in one place. This modal kept its own copy of the EOD state, the fetch
  // and every derived series. That is how a correctness fix landed in the hook and left the
  // modal -- the thing users actually look at -- still wrong. StableContractDeepPanel
  // already consumed the hook; now this does too.
  const {
    data,
    loading,
    error,
    dateRange,
    setDateRange,
    handleUpdateDates,
    tapeByDayLoading,
    thetaSecondOrder,
    thetaThirdOrder,
    loadingThetaGreeks,
    minDate,
    today,
    filteredByDate,
    primaryChartData,
    primaryLabel,
    tradesChartData,
    deltaChartData,
    gammaChartData,
    spotGammaChartData,
    volatilityChartData,
    boughtSoldPctData,
    openClosePctData,
    flowChartData,
    tapeByDayChartData,
    unusualFlow,
    averages,
    todaySnapshot,
  } = useContractHistoricalEod({ params, enabled: open, activeTab });

  /** Tape analytics (session-level); aligns with backend tape_summary. */
  type TapeSummary = {
    strategy_id?: string;
    strategy_note?: string;
    bid_side_contracts?: number;
    ask_side_contracts?: number;
    between_contracts?: number;
    unknown_contracts?: number;
    trades_count?: number;
    quote_ladder_points?: number;
    classified_pct?: number;
  };

  /** One row per trade (trades-only response + optional tape fields). */
  type IntradayRow = {
    time: string;
    datetime?: string;
    price?: number;
    size?: number;
    premium?: number;
    underlying_price?: number;
    condition_codes?: number[];
    condition_label?: string;
    condition_color?: string;
    condition_description?: string;
    exchange?: number;
    tape_side?: 'at_bid' | 'at_ask' | 'between' | 'unknown' | string;
    execution_side?: 'buy' | 'sell' | 'mid' | string;
    nbbo_bid?: number | null;
    nbbo_ask?: number | null;
    quote_ms_of_day?: number | null;
  };
  const [intradayCallData, setIntradayCallData] = useState<IntradayRow[]>([]);
  const [intradayPutData, setIntradayPutData] = useState<IntradayRow[]>([]);
  const primaryRightIsPut = (params?.optionType || '').toString().toLowerCase().startsWith('p');
  const intradayData = useMemo(
    () => (primaryRightIsPut ? intradayPutData : intradayCallData),
    [primaryRightIsPut, intradayPutData, intradayCallData],
  );
  const [intradayLoading, setIntradayLoading] = useState(false);
  const [intradayMessage, setIntradayMessage] = useState<string | null>(null);
  /** Date for intraday trades (same-day only). */
  const [intradayDate, setIntradayDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  /** Date returned by backend (may differ when fallback to previous trading day). */
  const [intradayResponseDate, setIntradayResponseDate] = useState<string | null>(null);
  /** How many chart points to render for intraday (user can increase when downsampling is active). */
  const [intradayChartPointLimit, setIntradayChartPointLimit] = useState(DEFAULT_INTRADAY_CHART_POINTS);
  /** Bump to force-refresh intraday data without changing the date. */
  const [intradayReloadToken, setIntradayReloadToken] = useState(0);
  /** Refs: Flow tape fetch must not depend on intraday state or it aborts mid-request when intraday updates. */
  const intradayDataRef = useRef<IntradayRow[]>(intradayData);
  const intradayDateRef = useRef(intradayDate);
  const intradayResponseDateRef = useRef<string | null>(intradayResponseDate);
  intradayDataRef.current = intradayData;
  intradayDateRef.current = intradayDate;
  intradayResponseDateRef.current = intradayResponseDate;

  /**
   * Chronological rows, unique `xKey` per point (duplicate HH:MM:SS breaks Recharts line charts),
   * signed premium from aggressor side, bar colors for size/premium-by-time.
   */
  const intradayChartPrepared = useMemo(() => {
    const dayYmd = intradayResponseDate?.slice(0, 10) || intradayDate?.slice(0, 10) || '';
    let indexed = intradayData.map((row, i) => ({ row, i }));
    indexed.sort((a, b) => {
      const da = intradayRowTimeMs(a.row, dayYmd);
      const db = intradayRowTimeMs(b.row, dayYmd);
      if (da !== db) return da - db;
      return a.i - b.i;
    });
    if (indexed.length > intradayChartPointLimit) {
      const step = Math.ceil(indexed.length / intradayChartPointLimit);
      indexed = indexed.filter((_, i) => i % step === 0);
    }
    const buyC = uiColors.chartSuccess;
    const sellC = uiColors.chartError;
    const neuC = uiColors.textMuted;
    return indexed.map(({ row }, chartIndex) => {
      const priceN = Number(row.price);
      const sizeN = Number(row.size);
      const price = Number.isFinite(priceN) ? priceN : undefined;
      const size = Number.isFinite(sizeN) ? sizeN : undefined;
      const absPrem = chartFiniteNumber(
        row.premium ?? (Number(price) || 0) * (Number(size) || 0) * 100,
        0
      );
      const sign = intradayAggressorSign(row);
      const signedPremiumUsd = sign === 0 ? 0 : sign * absPrem;
      const dirColor = sign === 1 ? buyC : sign === -1 ? sellC : neuC;
      const tickLabel = (row.time && String(row.time).trim()) || String(chartIndex);
      return {
        ...row,
        price,
        size,
        chartIndex,
        xKey: String(chartIndex),
        tickLabel,
        absPremiumUsd: absPrem,
        signedPremiumUsd,
        sizeForBar: Math.abs(Number(size) || 0),
        directionBarColor: dirColor,
      };
    });
  }, [intradayData, intradayDate, intradayResponseDate, uiColors.chartSuccess, uiColors.chartError, uiColors.textMuted]);

  const intradayCumulativeSeries = useMemo(() => {
    let cum = 0;
    return intradayChartPrepared.map((d) => {
      cum += d.signedPremiumUsd;
      return { xKey: d.xKey, tickLabel: d.tickLabel, cumulative: cum };
    });
  }, [intradayChartPrepared]);

  const intradayNetSignedUsd = useMemo(
    () => intradayChartPrepared.reduce((s, d) => s + d.signedPremiumUsd, 0),
    [intradayChartPrepared]
  );

  const intradaySessionDayYmd = intradayResponseDate?.slice(0, 10) || intradayDate?.slice(0, 10) || '';

  const intradayFlowBySideSeries = useMemo(
    () => downsampleUniform(buildIntradayFlowQuadrantCumulative(intradayCallData, intradayPutData, intradaySessionDayYmd), intradayChartPointLimit),
    [intradayCallData, intradayPutData, intradaySessionDayYmd, intradayChartPointLimit],
  );

  const intradayBuySellCumulative = useMemo(
    () => downsampleUniform(buildIntradayBuySellCumulative(intradayCallData, intradayPutData, intradaySessionDayYmd), intradayChartPointLimit),
    [intradayCallData, intradayPutData, intradaySessionDayYmd, intradayChartPointLimit],
  );

  const intradayTapeLocationCumulative = useMemo(
    () => downsampleUniform(buildIntradayTapeLocationCumulative(intradayCallData, intradayPutData, intradaySessionDayYmd), intradayChartPointLimit),
    [intradayCallData, intradayPutData, intradaySessionDayYmd, intradayChartPointLimit],
  );

  const intradayHasAnyTrades = intradayCallData.length > 0 || intradayPutData.length > 0;

  const [intradayTapeSummary, setIntradayTapeSummary] = useState<TapeSummary | null>(null);
  const [flowTrades, setFlowTrades] = useState<
    Array<{
      id?: number;
      time?: string;
      price?: number;
      volume?: number;
      side?: string;
      execution_side?: string;
      premium?: number;
      netFlow?: string;
      callNetFlow?: string;
      putNetFlow?: string;
      condition?: number;
      condition_codes?: number[];
      condition_label?: string;
      condition_name?: string;
      condition_description?: string;
      trade_payload_detail?: string;
      sequence?: number;
      exchange?: number;
      theta_payload_json?: string;
      theta_tick?: unknown;
      polygon_tick?: unknown;
      timestamp?: string;
      sessionDayYmd?: string;
      contract_ticker?: string;
      expiry?: string;
      strike?: number;
      optionType?: string;
    }>
  >([]);
  const [flowTradesLoading, setFlowTradesLoading] = useState(false);
  const [flowTradesError, setFlowTradesError] = useState<string | null>(null);
  /** Calendar range (YYYY-MM-DD) for contract tape on the Flow tab (passed to historical-trades). Default to last trading day. */
  const [flowDateFrom, setFlowDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [flowDateTo, setFlowDateTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  /** Flow tab only: hide prints below this premium (USD); client-side filter, does not change the API request. */
  const [flowMinPremium, setFlowMinPremium] = useState(0);
  /** Bump to re-fetch tape from the server (Load tape, or switch contract / open Flow tab). */
  const [flowReloadToken, setFlowReloadToken] = useState(0);
  /** After multi-page fetch: how many raw rows before the display min-premium filter. */
  const [flowTapeLoadInfo, setFlowTapeLoadInfo] = useState<{ raw: number; pages: number } | null>(null);
  const [flowPatternPanelOpen, setFlowPatternPanelOpen] = useState(false);
  const [flowPatternAnchorLeg, setFlowPatternAnchorLeg] = useState<NormalizedFlowLeg | null>(null);
  const [flowTablePage, setFlowTablePage] = useState(1);
  const FLOW_TABLE_PAGE_SIZE = 100;

  // Server-side page limit for flow tape fetch (starts small for heavy tickers; user can "Load More")
  const [flowServerPageLimit, setFlowServerPageLimit] = useState(() => (isHeavyTicker(params?.underlying) ? 3 : 80));
  const flowAccumulatedRawRef = useRef<any[]>([]);
  const flowPagesLoadedRef = useRef(0);
  const flowLastKeyRef = useRef('');

  // Shared smart-flow cache: key = "symbol|strike|expiry|optionType|date" → deduplicated trades
  // Both Intraday and Flow tabs use this so switching tabs doesn't re-fetch.
  const sfCacheRef = useRef<Map<string, { trades: any[]; ts: number }>>(new Map());
  const SF_CACHE_TTL_MS = 60_000; // 60s — data stays warm for quick tab switches

  // Shared fetch: both Intraday and Flow call this. Returns deduplicated raw smart-flow trades.
  const sfFetchInProgressRef = useRef<Map<string, Promise<any[]>>>(new Map());
  const ensureSmartFlowLoaded = useCallback(async (
    underlying: string, strike: string | number, expiry: string,
    optionType: string, date: string, signal?: AbortSignal
  ): Promise<any[]> => {
    const ot = optionType.toLowerCase().startsWith('p') ? 'put' : 'call';
    const cacheKey = `${resolveFuturesUnderlying(underlying)}|${strike}|${formatDate(expiry) || expiry}|${ot}|${date}`;

    // Check cache
    const cached = sfCacheRef.current.get(cacheKey);
    if (cached && (Date.now() - cached.ts) < SF_CACHE_TTL_MS) {
      return cached.trades;
    }

    // Deduplicate concurrent requests for the same key
    const inflight = sfFetchInProgressRef.current.get(cacheKey);
    if (inflight) return inflight;

    const fetchPromise = (async () => {
      const trades: any[] = [];
      try {
        const sfBase = typeof getApiUrl === 'function' ? getApiUrl('/api/smart-flow/historical-trades') : '/api/smart-flow/historical-trades';
        const sfUrlStr = sfBase.startsWith('http') ? sfBase : `${window.location.origin}${sfBase}`;
        for (let sfPage = 1; sfPage <= 100; sfPage++) {
          const sfUrl = new URL(sfUrlStr);
          sfUrl.searchParams.set('symbol', resolveFuturesUnderlying(underlying));
          sfUrl.searchParams.set('expiry', formatDate(expiry) || expiry);
          sfUrl.searchParams.set('strike', String(strike));
          sfUrl.searchParams.set('optionType', ot);
          sfUrl.searchParams.set('page', String(sfPage));
          sfUrl.searchParams.set('page_size', '5000');
          sfUrl.searchParams.set('date_from', date);
          sfUrl.searchParams.set('date_to', date);
          const r = await modalFetch(sfUrl.toString(), { signal }).then((r2) => r2.json()) as Record<string, unknown>;
          const chunk = (Array.isArray(r?.trades) ? r.trades : []) as any[];
          trades.push(...chunk);
          const pag = r?.pagination as { has_next_page?: boolean } | undefined;
          if (!pag?.has_next_page || chunk.length === 0) break;
        }
        // Deduplicate by sequence
        const seen = new Set<unknown>();
        const deduped = trades.filter((t) => {
          const seq = t.sequence ?? t.seq;
          if (seq == null) return true;
          if (seen.has(seq)) return false;
          seen.add(seq);
          return true;
        });
        sfCacheRef.current.set(cacheKey, { trades: deduped, ts: Date.now() });
        if (sfCacheRef.current.size > 10) {
          const entries = Array.from(sfCacheRef.current.entries());
          const oldest = entries.sort((a, b) => a[1].ts - b[1].ts)[0];
          if (oldest) sfCacheRef.current.delete(oldest[0]);
        }
        return deduped;
      } catch {
        return trades; // return whatever we got
      } finally {
        sfFetchInProgressRef.current.delete(cacheKey);
      }
    })();

    sfFetchInProgressRef.current.set(cacheKey, fetchPromise);
    return fetchPromise;
  }, []);

  // Reset flow table page whenever the dataset changes
  useEffect(() => {
    setFlowTablePage(1);
  }, [flowTrades.length, flowReloadToken]);

  // Reset server page limit when contract changes
  useEffect(() => {
    setFlowServerPageLimit(isHeavyTicker(params?.underlying) ? 3 : 80);
  }, [params?.underlying, params?.strike, params?.expiry, params?.optionType]);

  // Reset intraday chart point limit when contract or date changes
  useEffect(() => {
    setIntradayChartPointLimit(DEFAULT_INTRADAY_CHART_POINTS);
  }, [params?.underlying, params?.strike, params?.expiry, params?.optionType, intradayDate]);

  const flowPatternContractCtx = useMemo(() => {
    if (!params?.underlying || params.strike === undefined || !params?.expiry || params.expiry === 'all') return null;
    const underlying = resolveFuturesUnderlying(params.underlying.trim()).toUpperCase();
    const expiryYmd = String(params.expiry).replace(/\//g, '-').slice(0, 10);
    const strike = Number(params.strike);
    if (!Number.isFinite(strike)) return null;
    const right: FlowPatternRight = params.optionType?.toLowerCase() === 'put' ? 'P' : 'C';
    return { underlying, expiryYmd, strike, right };
  }, [params?.underlying, params?.strike, params?.expiry, params?.optionType]);

  const flowPatternDataset = useMemo(() => {
    if (!flowPatternContractCtx) return [];
    return flowTrades
      .map((t, i) => normalizeHistoricalTradeRow(t, flowPatternContractCtx, i))
      .filter((x): x is NormalizedFlowLeg => x != null);
  }, [flowTrades, flowPatternContractCtx]);

  const flowTapeContractKey = useMemo(() => {
    if (!params?.underlying || params?.strike == null || !params?.expiry || params?.expiry === 'all') return '';
    return `${resolveFuturesUnderlying(params.underlying)}|${params.strike}|${formatDate(params.expiry) || params.expiry}|${(params.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'put' : 'call'}`;
  }, [params?.underlying, params?.strike, params?.expiry, params?.optionType]);

  const flowDateRangeRef = useRef({ from: flowDateFrom, to: flowDateTo });
  useEffect(() => {
    flowDateRangeRef.current = { from: flowDateFrom, to: flowDateTo };
  }, [flowDateFrom, flowDateTo]);

  const prevTapeTabRef = useRef<string>('');
  const prevTapeContractRef = useRef<string>('');
  useEffect(() => {
    if (!open) {
      prevTapeTabRef.current = '';
      prevTapeContractRef.current = '';
      return;
    }
    if (activeTab !== 'flow' || !flowTapeContractKey) {
      prevTapeTabRef.current = activeTab;
      return;
    }
    const contractChanged = prevTapeContractRef.current !== flowTapeContractKey;
    prevTapeTabRef.current = activeTab;
    prevTapeContractRef.current = flowTapeContractKey;
    // Only reload on contract change, not on tab switch (data is preserved)
    if (contractChanged) {
      setFlowReloadToken((n) => n + 1);
    }
  }, [open, activeTab, flowTapeContractKey]);

  const loadFlowTape = useCallback(() => {
    setFlowReloadToken((n) => n + 1);
  }, []);

  const flowTradesForDisplay = useMemo(() => {
    // The feed re-ingests the same OPRA print repeatedly, so the raw tape shows one trade many
    // times over and inflates every total taken from it. Collapse on the exchange's own identity.
    const rows = dedupePrints(flowTrades);
    const min = Number(flowMinPremium);
    if (!Number.isFinite(min) || min <= 0) return rows;
    return rows.filter((t) => {
      const p = Number(t.premium ?? 0);
      return Number.isFinite(p) && p >= min;
    });
  }, [flowTrades, flowMinPremium]);

  /** How many re-ingested copies were hidden, so the count is disclosed rather than silent. */
  const flowDuplicateCount = useMemo(
    () => flowTrades.length - dedupePrints(flowTrades).length,
    [flowTrades],
  );

  /** Premium totals for Flow tab: uses table rows, or all loaded rows if min premium hides every row. */
  const flowTapePremiumSummary = useMemo(() => {
    const hasDisplay = flowTradesForDisplay.length > 0;
    const fallbackAll =
      !hasDisplay && flowMinPremium > 0 && flowTrades.length > 0 ? flowTrades : null;
    const rows = hasDisplay ? flowTradesForDisplay : fallbackAll ?? [];
    if (rows.length === 0) {
      return {
        hasSummary: false,
        caption: null as string | null,
        boughtPremium: 0,
        soldPremium: 0,
        midPremium: 0,
        directionalTotal: 0,
        pctBought: null as number | null,
        pctSold: null as number | null,
        premiumDelta: 0,
      };
    }
    let boughtPremium = 0;
    let soldPremium = 0;
    let midPremium = 0;
    for (const t of rows) {
      const prem = Number(t.premium ?? 0);
      const p = Number.isFinite(prem) && prem >= 0 ? prem : 0;
      const ag = flowTapeAggressionSide(t);
      if (ag === 'buy') boughtPremium += p;
      else if (ag === 'sell') soldPremium += p;
      else midPremium += p;
    }
    const directionalTotal = boughtPremium + soldPremium;
    const pctBought =
      directionalTotal > 0 ? (boughtPremium / directionalTotal) * 100 : null;
    const pctSold =
      directionalTotal > 0 ? (soldPremium / directionalTotal) * 100 : null;
    const premiumDelta = boughtPremium - soldPremium;
    let caption: string | null = null;
    if (fallbackAll && !hasDisplay) {
      caption =
        'Based on all loaded prints. Every row is below the display minimum; lower or clear the minimum to match the table.';
    } else if (flowMinPremium > 0 && hasDisplay) {
      caption = 'Based on the filtered table (display minimum premium).';
    }
    return {
      hasSummary: true,
      caption,
      boughtPremium,
      soldPremium,
      midPremium,
      directionalTotal,
      pctBought,
      pctSold,
      premiumDelta,
    };
  }, [flowTrades, flowTradesForDisplay, flowMinPremium]);

  const flowTablePagination = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(flowTradesForDisplay.length / FLOW_TABLE_PAGE_SIZE));
    const clampedPage = Math.min(flowTablePage, totalPages);
    const start = (clampedPage - 1) * FLOW_TABLE_PAGE_SIZE;
    return {
      totalPages,
      clampedPage,
      start,
      pageRows: flowTradesForDisplay.slice(start, start + FLOW_TABLE_PAGE_SIZE),
    };
  }, [flowTradesForDisplay, flowTablePage]);

  const intradayUrlCall = useMemo(() => {
    const base = typeof getApiUrl === 'function' ? getApiUrl('/api/historical/intraday-gamma-shift') : '/api/historical/intraday-gamma-shift';
    const url = base.startsWith('http') ? base : `${window.location.origin}${base}`;
    const u = new URL(url);
    u.searchParams.set('underlying', resolveFuturesUnderlying(params?.underlying || 'SPY'));
    const dateForRequest = intradayDate || dateRange.to || new Date().toISOString().slice(0, 10);
    u.searchParams.set('date', dateForRequest);
    if (params?.strike != null) u.searchParams.set('strike_eq', String(params.strike));
    const rawExpiry = params?.expiry && params.expiry !== 'all' ? params.expiry : '';
    const expNorm = rawExpiry ? formatDate(rawExpiry) : '';
    if (expNorm && /^\d{4}-\d{2}-\d{2}$/.test(expNorm)) u.searchParams.set('exp_date_eq', expNorm);
    if (params?.strike != null && expNorm) u.searchParams.set('type', 'call');
    return u.toString();
  }, [params?.underlying, params?.strike, params?.expiry, intradayDate, dateRange.to]);

  const intradayUrlPut = useMemo(() => {
    const base = typeof getApiUrl === 'function' ? getApiUrl('/api/historical/intraday-gamma-shift') : '/api/historical/intraday-gamma-shift';
    const url = base.startsWith('http') ? base : `${window.location.origin}${base}`;
    const u = new URL(url);
    u.searchParams.set('underlying', resolveFuturesUnderlying(params?.underlying || 'SPY'));
    const dateForRequest = intradayDate || dateRange.to || new Date().toISOString().slice(0, 10);
    u.searchParams.set('date', dateForRequest);
    if (params?.strike != null) u.searchParams.set('strike_eq', String(params.strike));
    const rawExpiry = params?.expiry && params.expiry !== 'all' ? params.expiry : '';
    const expNorm = rawExpiry ? formatDate(rawExpiry) : '';
    if (expNorm && /^\d{4}-\d{2}-\d{2}$/.test(expNorm)) u.searchParams.set('exp_date_eq', expNorm);
    if (params?.strike != null && expNorm) u.searchParams.set('type', 'put');
    return u.toString();
  }, [params?.underlying, params?.strike, params?.expiry, intradayDate, dateRange.to]);

  const intradayLoadedKeyRef = useRef<string>('');
  const intradayPrevReloadRef = useRef<number>(0);

  useEffect(() => {
    if (activeTab !== 'intraday' || !params?.underlying) {
      setIntradayLoading(false);
      return;
    }
    const contractKey = `${params.underlying}|${params.strike}|${params.expiry}|${intradayDate}`;
    const isRefresh = intradayPrevReloadRef.current !== intradayReloadToken;
    intradayPrevReloadRef.current = intradayReloadToken;

    // Skip if same contract+date and no refresh requested
    if (intradayLoadedKeyRef.current === contractKey && intradayData.length > 0 && !isRefresh) {
      return;
    }
    // Incremental refresh: only fetch page 1 and prepend new trades
    if (intradayLoadedKeyRef.current === contractKey && intradayData.length > 0 && isRefresh) {
      intradayLoadedKeyRef.current = contractKey; // keep key
      const abortCtrl = new AbortController();
      const timer = setTimeout(() => abortCtrl.abort(), 30_000);
      (async () => {
        try {
          const sfDate = (intradayDate || today).slice(0, 10);
          const sfOType = (params.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'put' : 'call';
          const sfBase = typeof getApiUrl === 'function' ? getApiUrl('/api/smart-flow/historical-trades') : '/api/smart-flow/historical-trades';
          const sfUrlStr = sfBase.startsWith('http') ? sfBase : `${window.location.origin}${sfBase}`;
          // Fetch page 1 (newest trades)
          const sfUrl = new URL(sfUrlStr);
          sfUrl.searchParams.set('symbol', resolveFuturesUnderlying(params.underlying));
          sfUrl.searchParams.set('expiry', formatDate(params.expiry) || params.expiry);
          sfUrl.searchParams.set('strike', String(params.strike));
          sfUrl.searchParams.set('optionType', sfOType);
          sfUrl.searchParams.set('page', '1');
          sfUrl.searchParams.set('page_size', '5000');
          sfUrl.searchParams.set('date_from', sfDate);
          sfUrl.searchParams.set('date_to', sfDate);
          const r = await modalFetch(sfUrl.toString(), { signal: abortCtrl.signal }).then((r2) => r2.json()) as Record<string, unknown>;
          const freshChunk = (Array.isArray(r?.trades) ? r.trades : []) as Array<Record<string, unknown>>;
          // Find the newest timestamp we already have
          const knownSeqs = new Set<unknown>();
          for (const t of intradayData) { if (t.datetime) knownSeqs.add(t.datetime); }
          // Filter out trades we already have (by timestamp)
          const newTrades = freshChunk.filter((t) => {
            const ts = String(t.timestamp ?? t.datetime ?? '');
            return !knownSeqs.has(ts);
          });
          if (newTrades.length > 0) {
            // Prepend new trades to existing data
            const toIntradayRow = (t: Record<string, unknown>): IntradayRow => {
              const ts2 = String(t.timestamp ?? t.datetime ?? '');
              const timeOnly = ts2.includes('T') ? ts2.split('T')[1]?.slice(0, 8) : ts2.slice(0, 8);
              const sz = Number(t.volume ?? t.quantity ?? t.size ?? 0);
              const px = Number(t.price ?? 0);
              const premRaw = Number(t.premium ?? 0);
              const ex = String(t.execution_side ?? t.side ?? '').toLowerCase();
              const tape = ex === 'buy' ? 'at_ask' as const : ex === 'sell' ? 'at_bid' as const : 'between' as const;
              return {
                time: timeOnly || '—', datetime: ts2, price: px, size: sz,
                premium: Number.isFinite(premRaw) && premRaw > 0 ? premRaw : (px * sz * 100),
                underlying_price: Number(t.underlying_price ?? 0),
                execution_side: (ex === 'buy' || ex === 'sell' || ex === 'mid') ? ex as 'buy' | 'sell' | 'mid' : undefined,
                tape_side: tape, condition_codes: [], condition_label: '', condition_color: '',
                condition_description: '', exchange: undefined, nbbo_bid: null, nbbo_ask: null, quote_ms_of_day: null,
              };
            };
            const newRows = newTrades.map(toIntradayRow);
            if (primaryRightIsPut) {
              setIntradayPutData((prev) => [...newRows.filter(r => r), ...prev]);
            } else {
              setIntradayCallData((prev) => [...newRows.filter(r => r), ...prev]);
            }
          }
        } catch { /* ignore incremental refresh errors */ }
        finally { clearTimeout(timer); setIntradayLoading(false); }
      })();
      return;
    }
    // Full fetch (new contract or date change)
    intradayLoadedKeyRef.current = contractKey;
    const abortCtrl = new AbortController();
    const timer = setTimeout(() => abortCtrl.abort(), 65_000);
    setIntradayLoading(true);
    setIntradayMessage(null);
    setIntradayResponseDate(null);
    setIntradayTapeSummary(null);

    const parseIntraday = (json: Record<string, unknown>) => ({
      ok: Boolean(json?.success && Array.isArray(json.data)),
      data: (Array.isArray(json?.data) ? json.data : []) as IntradayRow[],
      message: (json?.message as string) ?? null,
      date: (json?.date as string) ?? null,
      tape: (json?.tape_summary as TapeSummary) ?? null,
    });

    const doFetch = async () => {
      const sfDate = (intradayDate || today).slice(0, 10);
      const sfOType = (params.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'put' : 'call';
      const sfOTypeOpp = sfOType === 'put' ? 'call' : 'put';

      // Use shared fetch for BOTH sides
      const [sfPrimaryTrades0, sfOppositeTrades0] = await Promise.all([
        ensureSmartFlowLoaded(params.underlying, params.strike, params.expiry, sfOType, sfDate, abortCtrl.signal),
        ensureSmartFlowLoaded(params.underlying, params.strike, params.expiry, sfOTypeOpp, sfDate, abortCtrl.signal),
      ]);

      // Also fetch intraday API — gives us the response date when the selected day has no trades
      const [jCall, jPut] = await Promise.all([
        modalFetch(intradayUrlCall, { signal: abortCtrl.signal }).then((r) => r.json()),
        modalFetch(intradayUrlPut, { signal: abortCtrl.signal }).then((r) => r.json()),
      ]);
      const cCall = parseIntraday(jCall as Record<string, unknown>);
      const cPut = parseIntraday(jPut as Record<string, unknown>);
      const primary = primaryRightIsPut ? cPut : cCall;
      setIntradayMessage(primary.message ?? null);

      // ── If smart-flow was empty for the selected date, try the intraday API's response date ──
      // (e.g. user picks today but no trades yet; backend returns yesterday's data)
      let sfPrimaryTrades = sfPrimaryTrades0;
      let sfOppositeTrades = sfOppositeTrades0;
      const responseDate = (primary.date ?? '').slice(0, 10);
      if (sfPrimaryTrades0.length === 0 && responseDate && responseDate !== sfDate) {
        const [retryPri, retryOpp] = await Promise.all([
          ensureSmartFlowLoaded(params.underlying, params.strike, params.expiry, sfOType, responseDate, abortCtrl.signal),
          ensureSmartFlowLoaded(params.underlying, params.strike, params.expiry, sfOTypeOpp, responseDate, abortCtrl.signal),
        ]);
        sfPrimaryTrades = retryPri;
        sfOppositeTrades = retryOpp;
      }
      const sfAllTrades = sfPrimaryTrades;
      setIntradayResponseDate(sfAllTrades.length > 0 ? (sfPrimaryTrades0.length > 0 ? sfDate : responseDate) : (primary.date ?? null));

      // Convert smart-flow trades to IntradayRow format
      // Use the SAME side resolution as the Flow tab (finalizeFlowRows) so both tabs agree.
      const toIntraday = (t: Record<string, unknown>, right: 'C' | 'P'): IntradayRow => {
        const ts = String(t.timestamp ?? t.datetime ?? '');
        const timeOnly = ts.includes('T') ? ts.split('T')[1]?.slice(0, 8) : ts.slice(0, 8);
        const sz = Number(t.volume ?? t.quantity ?? t.size ?? 0);
        const px = Number(t.price ?? 0);
        const premRaw = Number(t.premium ?? 0);
        const prem = Number.isFinite(premRaw) && premRaw > 0 ? premRaw : (px * sz * 100);

        // ── Side resolution: mirror finalizeFlowRows exactly ──
        const exSide = String(t.execution_side ?? '').toLowerCase();
        const sideRaw = String(t.side ?? t.Side ?? 'mid').toLowerCase();
        const baseSide = (exSide === 'buy' || exSide === 'sell' || exSide === 'mid')
          ? exSide
          : (sideRaw === 'ask' ? 'buy' : sideRaw === 'bid' ? 'sell' : sideRaw);
        const side = baseSide === 'ask' ? 'buy' : baseSide === 'bid' ? 'sell' : baseSide;

        // Map resolved side → tape_side (same mapping the Flow tab implies)
        const tape: 'at_ask' | 'at_bid' | 'between' | 'unknown' =
          side === 'buy' ? 'at_ask' :
          side === 'sell' ? 'at_bid' :
          side === 'mid' ? 'between' :
          'unknown';

        return {
          time: timeOnly || '—',
          datetime: ts,
          price: px,
          size: sz,
          premium: prem,
          underlying_price: Number(t.underlying_price ?? t.underlyingPrice ?? 0),
          execution_side: (side === 'buy' || side === 'sell' || side === 'mid') ? side as 'buy' | 'sell' | 'mid' : undefined,
          tape_side: tape,
          condition_codes: [],
          condition_label: '',
          condition_color: '',
          condition_description: '',
          exchange: undefined,
          nbbo_bid: null,
          nbbo_ask: null,
          quote_ms_of_day: null,
        };
      };

      // Convert both sides: primary for stats/summary, both for Flow-by-side chart
      const sfCall = (primaryRightIsPut ? sfOppositeTrades : sfPrimaryTrades).map((t) => toIntraday(t, 'C'));
      const sfPut = (primaryRightIsPut ? sfPrimaryTrades : sfOppositeTrades).map((t) => toIntraday(t, 'P'));

      // ── Choose the source with MORE trades ──
      // The intraday API (Theta HTTP) is the authoritative source for tick-level trades.
      // Smart-flow (QuestDB) may have fewer trades when FPSS was down or the contract wasn't scanned.
      // Always use whichever has more data so the user sees the complete picture.
      const sfPrimaryCount = sfPrimaryTrades.length;
      const intraPrimaryCount = (primaryRightIsPut ? cPut.data : cCall.data).length;
      const useIntradayApi = intraPrimaryCount > sfPrimaryCount;

      // The data that will actually be displayed (used for tape summary too)
      let displayCallData: IntradayRow[] = cCall.data;
      let displayPutData: IntradayRow[] = cPut.data;

      if (useIntradayApi) {
        // Intraday API (Theta HTTP) has more trades — use it as primary.
        // Merge smart-flow side classification where sequences match.
        if (sfAllTrades.length > 0) {
          const sideBySeq = new Map<number, string>();
          for (const t of sfAllTrades as any[]) {
            const seq = Number(t.sequence ?? t.seq ?? 0);
            if (seq && t.execution_side) sideBySeq.set(seq, String(t.execution_side).toLowerCase());
          }
          const applySide = (rows: IntradayRow[]) => rows.map(r => {
            const seq = Number((r as any).sequence ?? 0);
            const sfSide = seq ? sideBySeq.get(seq) : undefined;
            if (sfSide === 'buy') return { ...r, execution_side: 'buy' as const, tape_side: 'at_ask' as const };
            if (sfSide === 'sell') return { ...r, execution_side: 'sell' as const, tape_side: 'at_bid' as const };
            return r;
          });
          displayCallData = applySide(cCall.data);
          displayPutData = applySide(cPut.data);
        }
      } else if (sfAllTrades.length > 0) {
        displayCallData = sfCall;
        displayPutData = sfPut;
      }

      setIntradayCallData(displayCallData);
      setIntradayPutData(displayPutData);

      // Compute tape summary from the SAME data we're displaying (after merge)
      const displayData = primaryRightIsPut ? displayPutData : displayCallData;
      {
        let bidVol = 0, askVol = 0, insideVol = 0, unknownVol = 0;
        for (const t of displayData) {
          const sz = Math.abs(Number(t.size) || 0);
          const ts = String(t.tape_side ?? '');
          if (ts === 'at_bid') bidVol += sz;
          else if (ts === 'at_ask') askVol += sz;
          else if (ts === 'between') insideVol += sz;
          else unknownVol += sz;
        }
        const total = bidVol + askVol + insideVol + unknownVol;
        const classified = bidVol + askVol + insideVol;
        setIntradayTapeSummary({
          strategy_id: useIntradayApi ? 'theta_http_intraday' : 'smart_flow_execution_side',
          strategy_note: useIntradayApi
            ? 'Trades from Theta HTTP REST (intraday API); side from smart-flow where available.'
            : 'Classified by execution side from smart-flow (same as Flow tab).',
          bid_side_contracts: bidVol,
          ask_side_contracts: askVol,
          between_contracts: insideVol,
          unknown_contracts: unknownVol,
          trades_count: displayData.length,
          quote_ladder_points: 0,
          classified_contracts: classified,
          classified_pct: total > 0 ? Math.round((classified / total) * 100) : 0,
        } as TapeSummary);
      }
    };

    doFetch()
      .catch(() => {
        setIntradayCallData([]);
        setIntradayPutData([]);
        setIntradayMessage(null);
        setIntradayTapeSummary(null);
      })
      .finally(() => {
        clearTimeout(timer);
        setIntradayLoading(false);
      });
    return () => {
      clearTimeout(timer);
      abortCtrl.abort();
    };
  }, [activeTab, params?.underlying, params?.strike, params?.expiry, params?.optionType, intradayUrlCall, intradayUrlPut, primaryRightIsPut, intradayReloadToken, intradayDate, today]);

  const buildFlowHistoricalTradesUrl = useCallback(
    (page: number, pageSize: number) => {
      if (!params?.underlying || params?.strike == null || !params?.expiry || params?.expiry === 'all') return null;
      const base = typeof getApiUrl === 'function' ? getApiUrl('/api/smart-flow/historical-trades') : '/api/smart-flow/historical-trades';
      const urlStr = base.startsWith('http') ? base : `${window.location.origin}${base}`;
      const u = new URL(urlStr);
      const { from, to } = flowDateRangeRef.current;
      u.searchParams.set('symbol', resolveFuturesUnderlying(params.underlying));
      u.searchParams.set('expiry', formatDate(params.expiry) || params.expiry);
      u.searchParams.set('strike', String(params.strike));
      u.searchParams.set('optionType', (params.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'put' : 'call');
      u.searchParams.set('page', String(Math.max(1, page)));
      u.searchParams.set('page_size', String(Math.max(1, Math.min(pageSize, 50000))));
      if (from) u.searchParams.set('date_from', from.slice(0, 10));
      if (to) u.searchParams.set('date_to', to.slice(0, 10));
      return u.toString();
    },
    [params?.underlying, params?.strike, params?.expiry, params?.optionType],
  );

  // Track what's already loaded so switching tabs doesn't re-fetch
  const flowLoadedKeyRef = useRef<string>('');

  useEffect(() => {
    if (!open) {
      // Only clear when modal closes
      setFlowTrades([]);
      setFlowTradesLoading(false);
      setFlowTradesError(null);
      setFlowTapeLoadInfo(null);
      flowLoadedKeyRef.current = '';
      return;
    }
    if (activeTab !== 'flow' || !flowTapeContractKey) {
      // Switched away from Flow — preserve data, just stop loading
      setFlowTradesLoading(false);
      return;
    }
    // Use shared data — same source as Intraday tab
    const sfDate = (flowDateFrom || today).slice(0, 10);
    const sfOType = (params?.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'put' : 'call';
    const contractKey = `${params?.underlying}|${params?.strike}|${params?.expiry}|${sfOType}|${sfDate}|${flowReloadToken}`;
    if (flowLoadedKeyRef.current === contractKey && flowTrades.length > 0) {
      return; // already loaded
    }
    flowLoadedKeyRef.current = contractKey;
    let cancelled = false;
    const abortCtrl = new AbortController();
    const timer = setTimeout(() => abortCtrl.abort(), 60_000);


    const finalizeFlowRows = (rawTrades: any[], apiMessage: string | null) => {
      const requestedExpiry = (formatDate(params?.expiry || '') || params?.expiry || '').replace(/-/g, '').slice(0, 8);
      const requestedStrike = Number(params?.strike ?? NaN);
      const requestedType = (params?.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'P' : 'C';
      const normalized = rawTrades.map((t: any, i: number) => {
        const vol = Number(t?.volume ?? t?.Volume ?? t?.size ?? t?.Size ?? t?.quantity ?? t?.Quantity ?? 0);
        const premRaw = Number(t?.premium ?? t?.Premium ?? t?.netFlow ?? 0);
        const priceRaw = Number(t?.price ?? t?.Price ?? t?.Last ?? 0);
        const premium = Number.isFinite(premRaw) && premRaw > 0
          ? premRaw
          : (Number.isFinite(priceRaw) && Number.isFinite(vol) ? priceRaw * vol * 100 : 0);
        const ts = String(t?.timestamp ?? t?.time ?? t?.Time ?? t?.tradetime ?? t?.date ?? '');
        let sessionDayYmd = '';
        if (/^\d{4}-\d{2}-\d{2}/.test(ts)) {
          sessionDayYmd = ts.slice(0, 10);
        } else if (typeof t?.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(String(t.date))) {
          sessionDayYmd = String(t.date).slice(0, 10);
        } else {
          const dr = flowDateRangeRef.current;
          const df = dr.from?.slice(0, 10);
          const dt = dr.to?.slice(0, 10);
          if (df && df === dt) sessionDayYmd = df;
        }
        const exSide = String(t?.execution_side ?? '').toLowerCase();
        const sideRaw = String(t?.side ?? t?.Side ?? 'mid').toLowerCase();
        const baseSide = exSide === 'buy' || exSide === 'sell' || exSide === 'mid'
          ? exSide
          : (sideRaw === 'ask' ? 'buy' : sideRaw === 'bid' ? 'sell' : sideRaw);
        const side = baseSide === 'ask' ? 'buy' : baseSide === 'bid' ? 'sell' : baseSide;
        const numOpt = (v: unknown) =>
          v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined;
        const codesRaw = t?.condition_codes;
        const condition_codes = Array.isArray(codesRaw)
          ? codesRaw.map((x: unknown) => Number(x)).filter((n: number) => Number.isFinite(n))
          : undefined;
        return {
          id: t?.id ?? i + 1,
          contract_ticker: String(t?.contract_ticker ?? t?.Contract ?? t?.contract ?? t?.Symbol ?? ''),
          timestamp: ts,
          time: t?.time ?? t?.Time ?? (ts.includes('T') ? ts.split('T')[1]?.slice(0, 8) : ts),
          side,
          execution_side: typeof t?.execution_side === 'string' ? t.execution_side : undefined,
          price: Number.isFinite(priceRaw) ? priceRaw : 0,
          quantity: vol,
          premium: Number.isFinite(premium) ? premium : 0,
          volume: vol,
          netFlow: String(Number.isFinite(premium) ? premium : 0),
          callNetFlow: String((String(t?.['Option Type'] ?? t?.option_type ?? t?.optionType ?? '').toUpperCase().startsWith('C')) ? premium : 0),
          putNetFlow: String((String(t?.['Option Type'] ?? t?.option_type ?? t?.optionType ?? '').toUpperCase().startsWith('P')) ? premium : 0),
          expiry: String(t?.['Expiry Date'] ?? t?.expiry ?? ''),
          strike: Number(t?.Strike ?? t?.strike ?? 0),
          optionType: String(t?.['Option Type'] ?? t?.option_type ?? t?.optionType ?? '').toUpperCase().slice(0, 1),
          condition: numOpt(t?.condition ?? t?.['Trade Condition']),
          condition_codes,
          condition_label: typeof t?.condition_label === 'string' ? t.condition_label : undefined,
          condition_name: typeof t?.condition_name === 'string' ? t.condition_name : undefined,
          condition_description: typeof t?.condition_description === 'string' ? t.condition_description : undefined,
          trade_payload_detail: typeof t?.trade_payload_detail === 'string' ? t.trade_payload_detail : undefined,
          sequence: numOpt(t?.sequence),
          exchange: numOpt(t?.exchange),
          theta_payload_json: typeof t?.theta_payload_json === 'string' ? t.theta_payload_json : undefined,
          theta_tick: t?.theta_tick,
          polygon_tick: t?.polygon_tick,
          sessionDayYmd: sessionDayYmd || undefined,
        };
      });
      const filtered = normalized.filter((r: any) => {
        const rExp = String(r?.expiry || '').replace(/-/g, '').slice(0, 8);
        const rStrike = Number(r?.strike ?? NaN);
        const rType = String(r?.optionType || '').toUpperCase().slice(0, 1);
        const expOk = !requestedExpiry || !rExp || rExp === requestedExpiry;
        const strikeOk =
          !Number.isFinite(requestedStrike) || !Number.isFinite(rStrike) || Math.abs(rStrike - requestedStrike) < 0.01;
        const typeOk = requestedType ? (rType === requestedType) : true;
        return expOk && strikeOk && typeOk;
      });
      let displayList = filtered.length > 0 ? filtered : normalized;
      const dr = flowDateRangeRef.current;
      const idSession = intradayResponseDateRef.current || intradayDateRef.current || '';
      const sessionForIntraday = idSession.slice(0, 10);
      const inFlowRange =
        Boolean(sessionForIntraday) &&
        (!dr.from || sessionForIntraday >= dr.from.slice(0, 10)) &&
        (!dr.to || sessionForIntraday <= dr.to.slice(0, 10));
      const idRows = intradayDataRef.current;
      if (displayList.length === 0 && inFlowRange && idRows.length > 0 && params?.underlying) {
        const expCompact = (formatDate(params.expiry || '') || params.expiry || '').replace(/-/g, '').slice(0, 8);
        const strikeN = Number(params.strike ?? NaN);
        displayList = idRows.map((d, i) => {
          const sz = Number(d.size ?? 0);
          const px = Number(d.price ?? 0);
          const prem = Number(d.premium ?? (px * sz * 100));
          const ex = String(d.execution_side || '').toLowerCase();
          let side = ex === 'buy' || ex === 'sell' || ex === 'mid' ? ex : 'mid';
          if ((!ex || side === 'mid') && d.tape_side === 'at_ask') side = 'buy';
          else if ((!ex || side === 'mid') && d.tape_side === 'at_bid') side = 'sell';
          const day = sessionForIntraday;
          const ts = d.datetime || (day && d.time ? `${day}T${d.time}` : d.time || '');
          return {
            id: i + 1,
            contract_ticker: '',
            timestamp: ts,
            sessionDayYmd: sessionForIntraday || undefined,
            time: d.time ?? '—',
            side,
            execution_side: side,
            price: px,
            quantity: sz,
            premium: prem,
            volume: sz,
            netFlow: String(prem),
            callNetFlow: requestedType === 'C' ? String(prem) : '0',
            putNetFlow: requestedType === 'P' ? String(prem) : '0',
            expiry: expCompact,
            strike: strikeN,
            optionType: requestedType,
            condition: undefined,
            condition_codes: d.condition_codes,
            condition_label: d.condition_label,
            condition_name: undefined,
            condition_description: d.condition_description,
            trade_payload_detail: undefined,
            sequence: undefined,
            exchange: undefined,
            theta_payload_json: undefined,
            theta_tick: undefined,
            polygon_tick: undefined,
          };
        });
      }
      return { displayList, message: apiMessage };
    };

    (async () => {
      const currentKey = `${flowTapeContractKey}|${flowReloadToken}`;
      const isFreshFetch = flowLastKeyRef.current !== currentKey;
      if (isFreshFetch) {
        flowLastKeyRef.current = currentKey;
        flowAccumulatedRawRef.current = [];
        flowPagesLoadedRef.current = 0;
      }

      // Check shared cache first — if Intraday already fetched this data, use it
      if (isFreshFetch && params?.underlying && params?.strike != null && params?.expiry) {
        const sfDate = (flowDateFrom || today).slice(0, 10);
        const sfOType = (params.optionType || 'call').toString().toLowerCase().startsWith('p') ? 'put' : 'call';
        const sfCacheKey = `${resolveFuturesUnderlying(params.underlying)}|${params.strike}|${formatDate(params.expiry) || params.expiry}|${sfOType}|${sfDate}`;
        const cached = sfCacheRef.current.get(sfCacheKey);
        if (cached && (Date.now() - cached.ts) < SF_CACHE_TTL_MS && cached.trades.length > 0) {
          const { displayList } = finalizeFlowRows(cached.trades as any[], null);
          setFlowTrades(displayList);
          setFlowTapeLoadInfo({ raw: cached.trades.length, pages: 0 });
          setFlowTradesError(null);
          setFlowTradesLoading(false);
          return;
        }
      }

      setFlowTradesLoading(true);
      setFlowTradesError(null);

      try {
        // Use shared smart-flow data (same as Intraday tab)
        const sfTrades = await ensureSmartFlowLoaded(
          params.underlying, params.strike, params.expiry, sfOType, sfDate, abortCtrl.signal
        );
        flowAccumulatedRawRef.current = sfTrades;
        flowPagesLoadedRef.current = sfTrades.length > 0 ? 1 : 0;
        if (sfTrades.length === 0) {
          // ── Fallback: pull from intraday API (Theta HTTP) when smart-flow has no data ──
          // This happens when FPSS was down and the contract wasn't in the fallback worker's scan.
          // The intraday API pulls directly from Theta HTTP v3, so it always has data if Theta does.
          try {
            const ot = sfOType;
            const intradayBase = typeof getApiUrl === 'function' ? getApiUrl('/api/historical/intraday-gamma-shift') : '/api/historical/intraday-gamma-shift';
            const intraUrl = intradayBase.startsWith('http') ? intradayBase : `${window.location.origin}${intradayBase}`;
            const u = new URL(intraUrl);
            u.searchParams.set('underlying', resolveFuturesUnderlying(params.underlying || 'SPY'));
            u.searchParams.set('date', sfDate);
            if (params?.strike != null) u.searchParams.set('strike_eq', String(params.strike));
            const expNorm = formatDate(params?.expiry || '');
            if (expNorm && /^\d{4}-\d{2}-\d{2}$/.test(expNorm)) u.searchParams.set('exp_date_eq', expNorm);
            if (params?.strike != null && expNorm) u.searchParams.set('type', ot === 'put' ? 'put' : 'call');
            const intraResp = await modalFetch(u.toString(), { signal: abortCtrl.signal }).then((r) => r.json()) as Record<string, unknown>;
            const intraData = (Array.isArray(intraResp?.data) ? intraResp.data : []) as any[];
            const intraDate = String(intraResp?.date || '').slice(0, 10);
            if (intraData.length > 0) {
              // Convert intraday API rows to smart-flow-like trade objects for finalizeFlowRows
              const flowLikeTrades = intraData.map((d: any, i: number) => ({
                id: i + 1,
                contract_ticker: '',
                timestamp: d.datetime || `${intraDate || sfDate}T${d.time}`,
                time: d.time,
                execution_side: d.execution_side,
                side: d.tape_side === 'at_ask' ? 'buy' : d.tape_side === 'at_bid' ? 'sell' : (d.execution_side || 'mid'),
                Side: d.tape_side === 'at_ask' ? 'Ask' : d.tape_side === 'at_bid' ? 'Bid' : 'Mid',
                price: d.price,
                volume: d.size,
                quantity: d.size,
                premium: d.premium,
                netFlow: String(d.premium || 0),
                'Expiry Date': params?.expiry || '',
                Strike: params?.strike ?? 0,
                'Option Type': (params?.optionType || 'C').toUpperCase().slice(0, 1),
                condition: d.condition_codes?.[0],
                condition_codes: d.condition_codes,
                condition_label: d.condition_label,
                condition_description: d.condition_description,
                exchange: d.exchange,
                nbbo_bid: d.nbbo_bid,
                nbbo_ask: d.nbbo_ask,
              }));
              const { displayList, message } = finalizeFlowRows(flowLikeTrades, null);
              setFlowTrades(displayList);
              setFlowTapeLoadInfo({ raw: intraData.length, pages: 1 });
              setFlowTradesError(displayList.length === 0 ? (message || 'Trades found but filtered out.') : null);
            } else if (intraDate && intraDate !== sfDate) {
              // Intraday API returned a different date — retry smart-flow with that date
              const retrySf = await ensureSmartFlowLoaded(params.underlying, params.strike, params.expiry, sfOType, intraDate, abortCtrl.signal);
              if (retrySf.length > 0) {
                flowAccumulatedRawRef.current = retrySf;
                flowPagesLoadedRef.current = 1;
                const { displayList: dl2, message: msg2 } = finalizeFlowRows(retrySf, null);
                setFlowTrades(dl2);
                setFlowTapeLoadInfo({ raw: retrySf.length, pages: 1 });
                setFlowTradesError(dl2.length === 0 ? (msg2 || null) : null);
              } else {
                setFlowTrades([]);
                setFlowTradesError(`No flow data for this contract on ${sfDate} or ${intraDate}.`);
                setFlowTapeLoadInfo(null);
              }
            } else {
              setFlowTrades([]);
              setFlowTradesError('No flow data for this contract on this date.');
              setFlowTapeLoadInfo(null);
            }
          } catch {
            setFlowTrades([]);
            setFlowTradesError('No flow data for this contract on this date.');
            setFlowTapeLoadInfo(null);
          }
          return;
        }
        const { displayList, message } = finalizeFlowRows(sfTrades, null);
        setFlowTrades(displayList);
        setFlowTapeLoadInfo({ raw: sfTrades.length, pages: 1 });
        setFlowTradesError(displayList.length === 0 ? (message || null) : null);
      } catch (e) {
        if (cancelled) return;
        const aborted =
          (typeof DOMException !== 'undefined' && e instanceof DOMException && e.name === 'AbortError') ||
          (e instanceof Error && e.name === 'AbortError');
        if (aborted) return;
        setFlowTrades([]);
        setFlowTradesError('Unable to load this tape. Try again or narrow the date range.');
        setFlowTapeLoadInfo(null);
      } finally {
        clearTimeout(timer);
        setFlowTradesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      abortCtrl.abort();
    };
  }, [
    open,
    activeTab,
    flowTapeContractKey,
    flowReloadToken,
    flowServerPageLimit,
    buildFlowHistoricalTradesUrl,
    params?.underlying,
    params?.strike,
    params?.expiry,
    params?.optionType,
  ]);

  /** Fetch Theta second- and third-order Greeks; prefer one bar per day (24h), fall back to 1m series if daily is empty. */
  useEffect(() => {
    setPublishedOi(null);
    if (!open || !params?.underlying || params.strike == null || !params.expiry) return;
    const url = buildPublishedOiUrl(getApiUrl('/api/theta/snapshot'), {
      underlying: params.underlying,
      strike: Number(params.strike),
      expiry: String(params.expiry),
      optionType: params.optionType,
    });
    if (!url) return;
    const ac = new AbortController();
    modalFetch(url, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j) setPublishedOi(parsePublishedOi(j)); })
      .catch(() => { /* the table falls back to Pending, which is the honest default */ });
    return () => ac.abort();
  }, [open, params?.underlying, params?.strike, params?.expiry, params?.optionType]);

  // Contract or open-state changed: clear the state THIS modal owns. The EOD side belongs
  // to useContractHistoricalEod now and resets itself.
  useEffect(() => {
    if (!open || !params?.underlying || params.strike === undefined) {
      setIntradayCallData([]);
      setIntradayPutData([]);
      setIntradayLoading(false);
      setIntradayMessage(null);
      setIntradayResponseDate(null);
      setFlowTrades([]);
      setFlowTradesLoading(false);
      setFlowTradesError(null);
      return;
    }
    setIntradayDate(today);
    const lastTradeDay = (() => { const d = new Date(); d.setDate(d.getDate() - 1); while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })();
    setFlowDateFrom(lastTradeDay);
    setFlowDateTo(lastTradeDay);
  }, [open, params?.underlying, params?.strike, params?.expiry, params?.optionType, today]);

  const intradayTapeBarRow = useMemo(() => {
    if (!intradayTapeSummary) return [];
    const t = intradayTapeSummary;
    return [
      {
        label: 'Session',
        atBid: Number(t.bid_side_contracts) || 0,
        between: Number(t.between_contracts) || 0,
        atAsk: Number(t.ask_side_contracts) || 0,
      },
    ];
  }, [intradayTapeSummary]);

  if (!open) return null;

  const stroke = chartT.border;
  const gridStroke = chartT.grid;
  const axisTick = { fill: chartT.text, fontSize: 11, fontFamily: 'var(--font-mono)' };
  const historicalChartCardStyle: React.CSSProperties = {
    backgroundColor: 'var(--elev)',
    borderColor: 'var(--hair)',
    borderRadius: 10,
  };
  const rechartsTooltipContent: React.CSSProperties = {
    fontSize: 12,
    borderRadius: 8,
    background: 'var(--elev)',
    border: '1px solid var(--border)',
    color: 'var(--txt)',
    fontFamily: 'var(--font-ui)',
    boxShadow: 'var(--lift-2)',
  };
  const rechartsTooltipLabel: React.CSSProperties = { color: 'var(--txt)', fontWeight: 500 };
  const rechartsTooltipItem: React.CSSProperties = { color: 'var(--txt-2)' };
  const rechartsLegendStyle: React.CSSProperties = { color: 'var(--muted)', fontSize: 11 };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'SELECT' || active.getAttribute('role') === 'dialog')) return;
    onClose();
  };

  return (
    <>
      {typeof document !== 'undefined' &&
        createPortal(
          <FlowPatternRecognitionPanel
            open={flowPatternPanelOpen}
            onClose={() => {
              setFlowPatternPanelOpen(false);
              setFlowPatternAnchorLeg(null);
            }}
            anchor={flowPatternAnchorLeg}
            dataset={flowPatternDataset}
            language={language}
            overlayZClass="z-[110]"
            compact={compactChrome}
          />,
          document.body,
        )}
    <div
      className={
        compactChrome
          ? 'fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-4 bg-black/50'
          : 'fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50'
      }
      onClick={handleBackdropClick}
      onMouseDown={(e) => { if (e.target === e.currentTarget) e.stopPropagation(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={
          compactChrome
            ? 'w-full max-h-[92vh] sm:max-h-[90vh] sm:max-w-4xl overflow-hidden rounded-t-2xl sm:rounded-lg shadow-xl border'
            : 'max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-xl border'
        }
        style={{
          backgroundColor: 'var(--elev)',
          color: 'var(--txt)',
          borderColor: 'var(--border)',
          borderRadius: compactChrome ? undefined : 12,
          boxShadow: 'var(--lift-3)',
          fontFamily: 'var(--font-ui)',
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'var(--hair)', backgroundColor: 'var(--elev2)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-lg font-semibold" style={{ color: uiColors.text }}>
              Historical · {params?.underlying} {params?.strike} {params?.optionType || 'call'}
              {params?.expiry ? ` · ${params.expiry}` : ''}
              {!compactChrome && params?.source && (
                <span className="ml-2 text-sm font-normal opacity-80">(opened from {params.source})</span>
              )}
            </h3>
            {params?.underlying && params?.strike != null && params?.expiry && params.expiry !== 'all' && (
              <TrackButton
                symbol={String(params.underlying)}
                strike={Number(params.strike) || 0}
                expiry={String(params.expiry).slice(0, 10)}
                optionType={(params.optionType || 'call').toLowerCase().startsWith('p') ? 'P' : 'C'}
              />
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mmec-btn is-ghost"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-4 overflow-auto max-h-[calc(90vh-4rem)] space-y-6">
          <div className="flex gap-2 border-b pb-2" style={{ borderColor: uiColors.surfaceBorder }}>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={
                activeTab === 'eod'
                  ? { backgroundColor: uiColors.primary, color: uiColors.textInverse }
                  : { backgroundColor: uiColors.surfaceHover, color: uiColors.text, border: `1px solid ${uiColors.surfaceBorder}` }
              }
              onClick={() => setActiveTab('eod')}
            >
              Overall History
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={
                activeTab === 'intraday'
                  ? { backgroundColor: uiColors.primary, color: uiColors.textInverse }
                  : { backgroundColor: uiColors.surfaceHover, color: uiColors.text, border: `1px solid ${uiColors.surfaceBorder}` }
              }
              onClick={() => setActiveTab('intraday')}
            >
              Intraday
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={
                activeTab === 'flow'
                  ? { backgroundColor: uiColors.primary, color: uiColors.textInverse }
                  : { backgroundColor: uiColors.surfaceHover, color: uiColors.text, border: `1px solid ${uiColors.surfaceBorder}` }
              }
              onClick={() => setActiveTab('flow')}
            >
              Flow
            </button>
            <div className="flex-1" />
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: uiColors.surfaceHover,
                color: uiColors.text,
                border: `1px solid ${uiColors.surfaceBorder}`,
              }}
              onClick={() => {
                if (activeTab === 'eod') handleUpdateDates();
                else if (activeTab === 'intraday') setIntradayReloadToken((n) => n + 1);
                else if (activeTab === 'flow') loadFlowTape();
              }}
              title="Refresh current tab"
            >
              ↻ Refresh
            </button>
          </div>

          {activeTab === 'intraday' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium opacity-90" style={{ color: uiColors.text }}>
                  Date:
                </label>
                <input
                  type="date"
                  value={intradayDate}
                  onChange={(e) => setIntradayDate(e.target.value.slice(0, 10))}
                  className="rounded border px-2 py-1.5 text-sm"
                  style={{
                    backgroundColor: uiColors.surface,
                    borderColor: uiColors.surfaceBorder,
                    color: uiColors.text,
                  }}
                />
                <span className="text-sm opacity-80">One point per trade (up to 250). Price and size by time.</span>
              </div>
              {intradayResponseDate && intradayHasAnyTrades && (
                <p className="text-sm opacity-90">Trades for {intradayResponseDate}</p>
              )}

              {/* Did this session's flow open or close a position?
                  Derived from state already loaded — the tape gives the volume, the EOD rows
                  give open interest — so this costs no request. The EOD rows are fetched on the
                  eod tab, which is where the modal opens, so they are present by the time anyone
                  reaches this one; if they are not, the volume and the pending explanation still
                  render and only the open-interest comparison is omitted. */}
              {intradayHasAnyTrades && (() => {
                const day = (intradayResponseDate || intradayDate || '').slice(0, 10);
                if (!day) return null;
                const sessionVolume = intradayData.reduce((n, r) => n + (Number(r.size) || 0), 0);
                // What the tape itself says was left behind. 5,000 bought and 5,000 sold later
                // the same day is a position opened and closed inside the session, and open
                // interest will never show it — it reports no change at all for that day.
                const net = sessionNetPosition(intradayData);
                const outcome = sessionOutcome(net);
                const effects = eodPositionEffects(filteredByDate as EodOiRow[], {
                  // Settled by the figure OPRA published this morning, which reports the
                  // close of the session before it. Labelled with the stamp the endpoint
                  // returned rather than with the local clock, so a figure that settles a
                  // different session than expected is visible instead of misattributed.
                  publishedOi: publishedOi?.openInterest,
                  today: publishedOi?.publishedOn,
                });
                const e = effects.get(day);
                const eodRow = filteredByDate.find((r) => r.date === day);
                const oi = Number(eodRow?.open_interest) || 0;
                // Only `oi` contracts exist to be closed, so anything above that had to be created.
                const provableNew = oi > 0 && sessionVolume > oi ? sessionVolume - oi : null;
                const settled = e && !e.pending && e.label != null;
                const colour = settled
                  ? e!.label === 'Opening'
                    ? uiColors.chartSuccess
                    : e!.label === 'Closing'
                      ? uiColors.chartError
                      : uiColors.textMuted
                  : uiColors.textMuted;
                return (
                  <div className="rounded-xl p-3 border text-sm" style={historicalChartCardStyle}>
                    <span className="font-semibold" style={{ color: colour }}>
                      {settled
                        ? `${e!.label}${e!.change ? ` ${e!.change > 0 ? '+' : ''}${e!.change.toLocaleString()} contracts` : ''}`
                        : 'Open / close not settled yet'}
                    </span>
                    <span className="opacity-80">
                      {settled ? (
                        <> — confirmed by the open-interest figure dated {e!.confirmedBy}.</>
                      ) : (
                        <>
                          {' '}— open interest is published once, before the next session opens, so this
                          day&rsquo;s effect is not known until then.
                        </>
                      )}
                    </span>
                    {outcome && (
                      <div className="mt-2 text-xs">
                        <span className="font-semibold opacity-90">From the tape: </span>
                        <span
                          style={{
                            color:
                              outcome === 'Round-tripped'
                                ? uiColors.chartWarning
                                : outcome === 'Net long at close'
                                  ? uiColors.chartSuccess
                                  : outcome === 'Net short at close'
                                    ? uiColors.chartError
                                    : uiColors.textMuted,
                            fontWeight: 600,
                          }}
                        >
                          {outcome}
                        </span>
                        <span className="opacity-75">
                          {' '}— {net.buyVolume.toLocaleString()} bought, {net.sellVolume.toLocaleString()} sold,
                          peaking at {net.peakNet.toLocaleString()} and ending at{' '}
                          {net.netAtClose >= 0 ? '+' : ''}
                          {net.netAtClose.toLocaleString()}.
                          {net.roundTripped > 0 && (
                            <>
                              {' '}{net.roundTripped.toLocaleString()} contracts were taken on and given back
                              inside the session
                              {outcome === 'Round-tripped' && (
                                <> — opened and closed today, which open interest will report as no change</>
                              )}
                              .
                            </>
                          )}
                          {net.undirectedVolume > 0 && (
                            <> {net.undirectedVolume.toLocaleString()} traded with no resolved side.</>
                          )}
                        </span>
                        <div className="mt-1 opacity-60">
                          Sides are the tape aggressor, so a buy and a later sell may be one trader closing
                          out or two unrelated ones. It is the strongest read available before the next
                          open-interest print.
                        </div>
                      </div>
                    )}
                    <div className="mt-2 text-xs opacity-75">
                      {sessionVolume.toLocaleString()} contracts traded
                      {oi > 0 && <> against open interest of {oi.toLocaleString()}</>}.
                      {provableNew != null && (
                        <span style={{ color: uiColors.chartSuccess }}>
                          {' '}At least {provableNew.toLocaleString()} must be new — more traded than existed to
                          be closed.
                        </span>
                      )}
                      {!settled && (
                        <> Re-open this date after the next session and it will read Opening or Closing.</>
                      )}
                    </div>
                  </div>
                );
              })()}
              {intradayMessage && intradayHasAnyTrades && (
                <p className="text-sm opacity-80">{intradayMessage}</p>
              )}
              {intradayLoading && <p className="text-sm opacity-80">Loading intraday…</p>}
              {!intradayLoading && !intradayHasAnyTrades && (
                <p className="text-sm opacity-80">
                  {intradayMessage && intradayMessage.trim() ? intradayMessage.trim() : `No trades for this contract on ${intradayDate || 'the selected date'}. Try another date.`}
                </p>
              )}
              {!intradayLoading && intradayHasAnyTrades && (
                <>
                  {intradayCallData.length + intradayPutData.length > DEFAULT_INTRADAY_CHART_POINTS && (
                    <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: uiColors.warning }}>
                      <span>
                        {intradayCallData.length + intradayPutData.length} trades loaded.
                        {intradayCallData.length + intradayPutData.length > intradayChartPointLimit
                          ? ` Rendering ${intradayChartPointLimit.toLocaleString()} points.`
                          : ' All points rendered.'}
                      </span>
                      {intradayCallData.length + intradayPutData.length > intradayChartPointLimit && (
                        <button
                          type="button"
                          className="px-2 py-0.5 rounded border text-xs disabled:opacity-40"
                          style={{ borderColor: uiColors.surfaceBorder, backgroundColor: uiColors.surfaceHover, color: uiColors.text }}
                          disabled={intradayChartPointLimit >= 20000}
                          onClick={() => setIntradayChartPointLimit((n) => Math.min(20000, n * 2))}
                        >
                          Show more points
                        </button>
                      )}
                      {intradayChartPointLimit > DEFAULT_INTRADAY_CHART_POINTS && (
                        <button
                          type="button"
                          className="px-2 py-0.5 rounded border text-xs disabled:opacity-40"
                          style={{ borderColor: uiColors.surfaceBorder, backgroundColor: uiColors.surfaceHover, color: uiColors.text }}
                          onClick={() => setIntradayChartPointLimit(DEFAULT_INTRADAY_CHART_POINTS)}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                  {/* Summary: total trades, volume, premium — shows scale and net inflow/outflow context */}
                  <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                    <h4 className="text-sm font-semibold mb-3 opacity-90">Session summary</h4>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div><span className="opacity-75">Trades:</span> <strong>{intradayData.length}</strong></div>
                      <div><span className="opacity-75">Total volume:</span> <strong>{intradayData.reduce((s, d) => s + (d.size ?? 0), 0).toLocaleString()}</strong></div>
                      <div><span className="opacity-75">Total premium:</span> <strong>${intradayData.reduce((s, d) => s + (d.premium ?? (d.price ?? 0) * (d.size ?? 0) * 100), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong></div>
                      <div><span className="opacity-75">Net (buy − sell):</span>{' '}
                        <strong style={{ color: intradayNetSignedUsd >= 0 ? uiColors.chartSuccess : uiColors.chartError }}>
                          ${intradayNetSignedUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </strong>
                      </div>
                      <div><span className="opacity-75">Avg price:</span> <strong>${(intradayData.reduce((s, d) => s + (d.price ?? 0), 0) / (intradayData.length || 1)).toFixed(2)}</strong></div>
                      <div>
                        <span className="opacity-75">Prints at strike (call / put legs):</span>{' '}
                        <strong>
                          {intradayCallData.length} / {intradayPutData.length}
                        </strong>
                      </div>
                    </div>
                    <p className="text-xs opacity-75 mt-2">
                      Total premium is gross notional. Net uses print-side classification (buy adds, sell subtracts); unclassified prints do not move net.
                    </p>
                  </div>
                  {intradayTapeSummary && intradayTapeBarRow.length > 0 && (
                    <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                      <h4 className="text-sm font-semibold mb-2 opacity-90">Print-based volume (this session)</h4>
                      <p className="text-xs opacity-75 mb-3">
                        Contracts classified by trade price vs the latest NBBO at or before each print: at bid (sell aggressor), at ask (buy aggressor), inside spread.
                        {intradayTapeSummary.quote_ladder_points != null && (
                          <> Quote points merged: {intradayTapeSummary.quote_ladder_points}. </>
                        )}
                        {intradayTapeSummary.classified_pct != null && (
                          <> Classified: {intradayTapeSummary.classified_pct}% of session contracts.</>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm mb-3">
                        <div><span className="opacity-75">At bid:</span> <strong style={{ color: uiColors.chartError }}>{(intradayTapeSummary.bid_side_contracts ?? 0).toLocaleString()}</strong></div>
                        <div><span className="opacity-75">Inside:</span> <strong>{(intradayTapeSummary.between_contracts ?? 0).toLocaleString()}</strong></div>
                        <div><span className="opacity-75">At ask:</span> <strong style={{ color: uiColors.chartSuccess }}>{(intradayTapeSummary.ask_side_contracts ?? 0).toLocaleString()}</strong></div>
                        <div><span className="opacity-75">Unknown:</span> <strong>{(intradayTapeSummary.unknown_contracts ?? 0).toLocaleString()}</strong></div>
                      </div>
                      <ResponsiveContainer width="100%" height={120}>
                        <BarChart data={intradayTapeBarRow} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                          <CartesianGrid stroke={gridStroke} horizontal={false} />
                          <XAxis type="number" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} />
                          <YAxis type="category" dataKey="label" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={56} />
                          <Tooltip
                            contentStyle={rechartsTooltipContent}
                            formatter={(v: number, name: string) => [v.toLocaleString(), name === 'atBid' ? 'At bid' : name === 'atAsk' ? 'At ask' : 'Inside spread']}
                          />
                          <Legend wrapperStyle={rechartsLegendStyle} />
                          <Bar dataKey="atBid" stackId="s" name="At bid" fill={uiColors.chartError} />
                          <Bar dataKey="inside" stackId="s" name="Inside spread" fill={uiColors.textMuted} />
                          <Bar dataKey="atAsk" stackId="s" name="At ask" fill={uiColors.chartSuccess} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {intradayFlowBySideSeries.length > 0 && (
                    <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                      <h4 className="text-sm font-semibold mb-3 opacity-90">
                        Flow by side (cumulative contracts) · Strike {params?.strike}
                        {params?.expiry && params.expiry !== 'all' ? ` · Expiry ${params.expiry}` : ''}
                      </h4>
                      <p className="text-xs opacity-75 mb-2">
                        Call and put legs at this strike and expiry, merged in time order. Each print adds its size to the bucket for buy/sell and call/put when the aggressor side is known. Lines match the Overall History flow-by-side chart (session scale here is cumulative prints, not end-of-day book depth).
                      </p>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={intradayFlowBySideSeries} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                          <CartesianGrid stroke={gridStroke} vertical={false} />
                          <XAxis
                            dataKey="xKey"
                            stroke={stroke}
                            axisLine={{ stroke: chartT.border }}
                            tickLine={false}
                            tick={axisTick}
                            tickFormatter={(v) => intradayFlowBySideSeries[Number(v)]?.tickLabel ?? String(v)}
                          />
                          <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={48} />
                          <Tooltip
                            contentStyle={rechartsTooltipContent}
                            labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                            labelFormatter={(label) =>
                              `Time ${intradayFlowBySideSeries[Number(label)]?.tickLabel ?? String(label)}`
                            }
                          />
                          <Legend wrapperStyle={rechartsLegendStyle} />
                          <Line type="monotone" dataKey="boughtCall" name="Bought call" stroke={uiColors.boughtCalls} strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="soldCall" name="Sold call" stroke={uiColors.soldCalls} strokeDasharray="5 4" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="boughtPut" name="Bought put" stroke={uiColors.boughtPuts} strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="soldPut" name="Sold put" stroke={uiColors.soldPuts} strokeDasharray="5 4" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {intradayBuySellCumulative.length > 0 && (
                    <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                      <h4 className="text-sm font-semibold mb-3 opacity-90">Buy vs sell volume (cumulative, both legs)</h4>
                      <p className="text-xs opacity-75 mb-2">
                        Running total contracts where aggressor side is buy or sell. Mid prints are excluded from both lines.
                      </p>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={intradayBuySellCumulative} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                          <CartesianGrid stroke={gridStroke} vertical={false} />
                          <XAxis
                            dataKey="xKey"
                            stroke={stroke}
                            axisLine={{ stroke: chartT.border }}
                            tickLine={false}
                            tick={axisTick}
                            tickFormatter={(v) => intradayBuySellCumulative[Number(v)]?.tickLabel ?? String(v)}
                          />
                          <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={44} />
                          <Tooltip
                            contentStyle={rechartsTooltipContent}
                            labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                            labelFormatter={(label) =>
                              `Time ${intradayBuySellCumulative[Number(label)]?.tickLabel ?? String(label)}`
                            }
                          />
                          <Legend wrapperStyle={rechartsLegendStyle} />
                          <Line
                            type="monotone"
                            dataKey="buyVol"
                            name="Buy (known)"
                            stroke={uiColors.chartSuccess}
                            strokeWidth={2}
                            dot={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="sellVol"
                            name="Sell (known)"
                            stroke={uiColors.chartError}
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {intradayTapeLocationCumulative.length > 0 && (
                    <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                      <h4 className="text-sm font-semibold mb-3 opacity-90">Print location (cumulative contracts)</h4>
                      <p className="text-xs opacity-75 mb-2">
                        Cumulative contract count by where the trade priced vs the latest quote at the print (lift / hit / inside). Useful with session open-interest context on Overall History; not a full open-position ledger.
                      </p>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={intradayTapeLocationCumulative} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                          <CartesianGrid stroke={gridStroke} vertical={false} />
                          <XAxis
                            dataKey="xKey"
                            stroke={stroke}
                            axisLine={{ stroke: chartT.border }}
                            tickLine={false}
                            tick={axisTick}
                            tickFormatter={(v) => intradayTapeLocationCumulative[Number(v)]?.tickLabel ?? String(v)}
                          />
                          <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={44} />
                          <Tooltip
                            contentStyle={rechartsTooltipContent}
                            labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                            labelFormatter={(label) =>
                              `Time ${intradayTapeLocationCumulative[Number(label)]?.tickLabel ?? String(label)}`
                            }
                          />
                          <Legend wrapperStyle={rechartsLegendStyle} />
                          <Line type="monotone" dataKey="atAsk" name="At ask" stroke={uiColors.chartSuccess} strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="atBid" name="At bid" stroke={uiColors.chartError} strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="inside" name="Inside / other" stroke={uiColors.textMuted} strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                    <h4 className="text-sm font-semibold mb-3 opacity-90">Cumulative premium (side-sensitive net)</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={intradayCumulativeSeries} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                        <CartesianGrid stroke={gridStroke} vertical={false} />
                        <XAxis
                          dataKey="xKey"
                          stroke={stroke}
                          axisLine={{ stroke: chartT.border }}
                          tickLine={false}
                          tick={axisTick}
                          tickFormatter={(v) => intradayCumulativeSeries[Number(v)]?.tickLabel ?? String(v)}
                        />
                        <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={56} tickFormatter={(v) => `$${v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? (v / 1e3).toFixed(0) + 'k' : v}`} />
                        <Tooltip
                          contentStyle={rechartsTooltipContent}
                          labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                          formatter={(v: number) => [`$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, 'Cumulative net']}
                          labelFormatter={(label) =>
                            `Time ${intradayCumulativeSeries[Number(label)]?.tickLabel ?? String(label)}`
                          }
                        />
                        <ReferenceLine y={0} stroke={gridStroke} strokeOpacity={0.55} strokeDasharray="2 3" />
                        <Line type="monotone" dataKey="cumulative" name="Cumulative net premium" stroke={uiColors.chartSuccess} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                    <p className="text-xs opacity-75 mt-1">
                      Running sum of signed premium (buy prints add, sell prints subtract) in chronological order.
                    </p>
                  </div>
                  <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                    <h4 className="text-sm font-semibold mb-3 opacity-90">Trade price by time</h4>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={intradayChartPrepared} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                        <CartesianGrid stroke={gridStroke} vertical={false} />
                        <XAxis
                          dataKey="xKey"
                          stroke={stroke}
                          axisLine={{ stroke: chartT.border }}
                          tickLine={false}
                          tick={axisTick}
                          tickFormatter={(v) => intradayChartPrepared[Number(v)]?.tickLabel ?? String(v)}
                        />
                        <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={48} label={{ value: 'Price', angle: -90, position: 'insideLeft' }} />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const p = payload[0]?.payload as IntradayRow | undefined;
                            return (
                              <div
                                className="rounded-lg px-3 py-2 border"
                                style={{
                                  backgroundColor: uiColors.surface,
                                  borderColor: uiColors.surfaceBorder,
                                  color: uiColors.text,
                                  boxShadow: 'var(--lift-2)',
                                }}
                              >
                                <div className="font-medium">{p?.time}</div>
                                <div>Price: {p?.price ?? '—'}</div>
                                <div>Size: {p?.size ?? '—'}</div>
                                <div>Premium: {p?.premium ?? '—'}</div>
                                {p?.tape_side && (
                                  <div className="mt-1 text-xs" style={{ color: uiColors.textSecondary }}>
                                    Print: {p.tape_side === 'at_bid' ? 'at bid' : p.tape_side === 'at_ask' ? 'at ask' : p.tape_side === 'between' ? 'inside spread' : p.tape_side}
                                    {p.nbbo_bid != null && p.nbbo_ask != null && (
                                      <span className="opacity-90"> · NBBO {p.nbbo_bid} / {p.nbbo_ask}</span>
                                    )}
                                  </div>
                                )}
                                {p?.execution_side && (
                                  <div className="text-xs mt-0.5 opacity-90">
                                    Aggressor: {String(p.execution_side).toUpperCase()}
                                  </div>
                                )}
                                {(p?.condition_label != null || (p?.condition_codes?.length ?? 0) > 0) && (
                                  <div className="mt-1 pt-1" style={{ borderTop: `1px solid ${uiColors.surfaceBorder}` }}>
                                    <div className="flex items-center gap-2">
                                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p?.condition_color ?? uiColors.textMuted }} />
                                      <span>{p?.condition_label ?? '—'}{p?.condition_codes?.length ? ` [${p.condition_codes.join(', ')}]` : ''}</span>
                                    </div>
                                    {p?.condition_description && <div className="text-xs opacity-85 mt-1">{p.condition_description}</div>}
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          name="Price"
                          stroke={uiColors.chartPrimary}
                          strokeWidth={1.75}
                          connectNulls
                          dot={(props: any) => {
                            const { cx, cy, payload } = props;
                            if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
                            const fill = payload?.condition_color || uiColors.chartPrimary;
                            return <circle cx={cx} cy={cy} r={2.5} fill={fill} stroke={uiColors.background} strokeWidth={1} />;
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                    <h4 className="text-sm font-semibold mb-3 opacity-90">Trade size by time</h4>
                    <p className="text-xs opacity-75 mb-2">Bar height = contracts; green = buy-side print, red = sell-side, gray = unclassified.</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={intradayChartPrepared} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                        <CartesianGrid stroke={gridStroke} vertical={false} />
                        <XAxis
                          dataKey="xKey"
                          stroke={stroke}
                          axisLine={{ stroke: chartT.border }}
                          tickLine={false}
                          tick={axisTick}
                          tickFormatter={(v) => intradayChartPrepared[Number(v)]?.tickLabel ?? String(v)}
                        />
                        <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={40} label={{ value: 'Size', angle: -90, position: 'insideLeft' }} />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const p = payload[0]?.payload as IntradayRow | undefined;
                            return (
                              <div
                                className="rounded-lg px-3 py-2 border"
                                style={{
                                  backgroundColor: uiColors.surface,
                                  borderColor: uiColors.surfaceBorder,
                                  color: uiColors.text,
                                  boxShadow: 'var(--lift-2)',
                                }}
                              >
                                <div className="font-medium">{p?.time}</div>
                                <div>Size: {p?.size ?? '—'}</div>
                                <div>Price: {p?.price ?? '—'}</div>
                                <div>Premium: {p?.premium ?? '—'}</div>
                                {p?.tape_side && (
                                  <div className="mt-1 text-xs" style={{ color: uiColors.textSecondary }}>
                                    Print: {p.tape_side === 'at_bid' ? 'at bid' : p.tape_side === 'at_ask' ? 'at ask' : p.tape_side === 'between' ? 'inside spread' : p.tape_side}
                                    {p.nbbo_bid != null && p.nbbo_ask != null && (
                                      <span className="opacity-90"> · NBBO {p.nbbo_bid} / {p.nbbo_ask}</span>
                                    )}
                                  </div>
                                )}
                                {p?.execution_side && (
                                  <div className="text-xs mt-0.5 opacity-90">
                                    Aggressor: {String(p.execution_side).toUpperCase()}
                                  </div>
                                )}
                                {(p?.condition_label != null || (p?.condition_codes?.length ?? 0) > 0) && (
                                  <div className="mt-1 pt-1" style={{ borderTop: `1px solid ${uiColors.surfaceBorder}` }}>
                                    <div className="flex items-center gap-2">
                                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p?.condition_color ?? uiColors.textMuted }} />
                                      <span>{p?.condition_label ?? '—'} {p?.condition_codes?.length ? `[${p.condition_codes.join(', ')}]` : ''}</span>
                                    </div>
                                    {p?.condition_description && <div className="text-xs opacity-85 mt-1">{p.condition_description}</div>}
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        />
                        <Bar dataKey="sizeForBar" name="Size" radius={[2, 2, 0, 0]}>
                          {intradayChartPrepared.map((entry) => (
                            <Cell key={entry.xKey} fill={entry.directionBarColor} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                    <h4 className="text-sm font-semibold mb-3 opacity-90">Premium by time (signed)</h4>
                    <p className="text-xs opacity-75 mb-2">Positive bars = buy-notional, negative = sell-notional; gray = unclassified.</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={intradayChartPrepared} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                        <CartesianGrid stroke={gridStroke} vertical={false} />
                        <XAxis
                          dataKey="xKey"
                          stroke={stroke}
                          axisLine={{ stroke: chartT.border }}
                          tickLine={false}
                          tick={axisTick}
                          tickFormatter={(v) => intradayChartPrepared[Number(v)]?.tickLabel ?? String(v)}
                        />
                        <YAxis
                          stroke={stroke}
                          axisLine={{ stroke: chartT.border }}
                          tickLine={false}
                          tick={axisTick}
                          width={52}
                          tickFormatter={(v) => `$${v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? (v / 1e3).toFixed(0) + 'k' : v}`}
                        />
                        <ReferenceLine y={0} stroke={gridStroke} strokeOpacity={0.55} strokeDasharray="2 3" />
                        <Tooltip
                          contentStyle={rechartsTooltipContent}
                          labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                          formatter={(v: number) => [`$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, 'Signed premium']}
                          labelFormatter={(label) => `Time ${intradayChartPrepared[Number(label)]?.tickLabel ?? String(label)}`}
                        />
                        <Bar dataKey="signedPremiumUsd" name="Signed premium" radius={[2, 2, 0, 0]}>
                          {intradayChartPrepared.map((entry) => (
                            <Cell key={`prem-${entry.xKey}`} fill={entry.directionBarColor} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'flow' && (
            <div className="space-y-4">
              <p className="text-sm opacity-90">
                Contract tape for this strike and expiry on one session day at a time. Default is today. Pick a specific day, optionally set a display minimum premium, then press <span className="font-semibold">Load tape</span> to pull all pages from the server for that day. This now matches Intraday behavior for direct day-by-day comparison.
              </p>
              <div
                className="rounded-2xl border p-4 space-y-3"
                style={{ borderColor: uiColors.surfaceBorder, backgroundColor: uiColors.surface }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-sm font-semibold opacity-95">Tape filters</div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium opacity-80">Session day</label>
                    <input
                      type="date"
                      value={flowDateFrom.slice(0, 10)}
                      min={minDate}
                      max={today}
                      onChange={(e) => {
                        const day = e.target.value.slice(0, 10);
                        setFlowDateFrom(day);
                        setFlowDateTo(day);
                      }}
                      className="rounded-lg border px-2 py-2 text-sm min-w-[10.5rem]"
                      style={{
                        backgroundColor: uiColors.surface,
                        borderColor: uiColors.surfaceBorder,
                        color: uiColors.text,
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium opacity-80" title="Hides smaller rows in the table below only; does not change the server request.">
                      Display min premium (USD)
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step={500}
                      value={flowMinPremium <= 0 ? '' : flowMinPremium}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setFlowMinPremium(Number.isFinite(v) && v > 0 ? v : 0);
                      }}
                      placeholder="0 = show all"
                      className="w-36 rounded-lg border px-2 py-2 text-sm"
                      style={{
                        backgroundColor: uiColors.surface,
                        borderColor: uiColors.surfaceBorder,
                        color: uiColors.text,
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={loadFlowTape}
                    disabled={flowTradesLoading || !flowTapeContractKey}
                    className="mmec-btn is-active disabled:opacity-50"
                    style={{ height: 32, fontSize: 12 }}
                  >
                    {flowTradesLoading ? 'Loading…' : 'Load tape'}
                  </button>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Display min premium only filters the table in your browser. Changing From/To does not reload until you press Load tape (or switch contract / re-open this tab).
                </p>
              </div>
              {flowTapeLoadInfo && flowTapeLoadInfo.raw > 0 && (
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Loaded {flowTapeLoadInfo.raw.toLocaleString()} print{flowTapeLoadInfo.raw === 1 ? '' : 's'} from the server ({flowTapeLoadInfo.pages} request{flowTapeLoadInfo.pages === 1 ? '' : 's'}).
                  {flowDuplicateCount > 0 && (
                    <span
                      title="The feed stores the same OPRA print more than once. Copies are collapsed on the exchange's own sequence so the tape and its totals count each trade once."
                    >
                      {' '}
                      {flowDuplicateCount.toLocaleString()} re-ingested cop
                      {flowDuplicateCount === 1 ? 'y' : 'ies'} collapsed.
                    </span>
                  )}
                </p>
              )}
              {flowTapeLoadInfo && flowTapeLoadInfo.raw > 0 && isHeavyTicker(params?.underlying) && flowServerPageLimit < 80 && (
                <button
                  type="button"
                  disabled={flowTradesLoading}
                  onClick={() => setFlowServerPageLimit((n) => Math.min(80, n + 3))}
                  className="text-xs px-3 py-1.5 rounded border font-medium disabled:opacity-40"
                  style={{
                    borderColor: uiColors.surfaceBorder,
                    backgroundColor: uiColors.surfaceHover,
                    color: uiColors.text,
                  }}
                >
                  {flowTradesLoading ? 'Loading more…' : 'Load more from server'}
                </button>
              )}
              {flowTradesForDisplay.length > 0 && (
                <div className="rounded-2xl p-4 border" style={historicalChartCardStyle}>
                  <SteppedOrderFlowChart
                    title="Historical TSX Options Flow - Buy/Sell Volume on Price Line"
                    trades={flowTradesForDisplay.slice(0, 5000).map((t, idx) => ({
                      id: t.id ?? idx,
                      time: String(t.time ?? ''),
                      sessionDayYmd: flowTapeRowDate(t),
                      timestamp: t.timestamp,
                      opra_trade_timestamp: t.timestamp,
                      price: chartFiniteNumber(t.price, 0),
                      volume: chartFiniteNumber(t.volume, 0),
                      side: flowTapeAggressionSide(t),
                      premium: chartFiniteNumber(t.premium, 0),
                    }))}
                  />
                </div>
              )}
              {flowTrades.length > 0 && flowMinPremium > 0 && (
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Showing {flowTradesForDisplay.length.toLocaleString()} of {flowTrades.length.toLocaleString()} rows (display minimum {flowMinPremium.toLocaleString()} USD premium).
                </p>
              )}
              {flowTapePremiumSummary.hasSummary && (
                <div
                  className="rounded-2xl border p-4 space-y-2"
                  style={{ borderColor: 'var(--hair)', backgroundColor: 'var(--elev)', borderRadius: 10 }}
                >
                  <div className="text-sm font-semibold opacity-95">Premium summary</div>
                  {flowTapePremiumSummary.caption && (
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {flowTapePremiumSummary.caption}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 sm:gap-4 items-stretch">
                    <div
                      className="min-w-[10rem] flex-1 rounded-lg border px-3 py-2"
                      style={{ background: 'var(--up-dim)', borderColor: 'color-mix(in srgb, var(--up) 40%, transparent)' }}
                    >
                      <div className="text-[11px] font-medium uppercase tracking-wide opacity-80" style={{ color: uiColors.chartSuccess }}>
                        Bought
                      </div>
                      <div className="text-lg font-semibold tabular-nums" style={{ color: uiColors.chartSuccess, fontFamily: 'var(--font-mono)' }}>
                        ${formatPremiumMillions(flowTapePremiumSummary.boughtPremium)}
                      </div>
                      <div className="text-xs tabular-nums" style={{ color: 'var(--muted)' }}>
                        {flowTapePremiumSummary.pctBought != null
                          ? `${flowTapePremiumSummary.pctBought.toFixed(1)}% of buy+sell premium`
                          : '—'}
                      </div>
                    </div>
                    <div
                      className="min-w-[10rem] flex-1 rounded-lg border px-3 py-2"
                      style={{ background: 'var(--down-dim)', borderColor: 'color-mix(in srgb, var(--down) 40%, transparent)' }}
                    >
                      <div className="text-[11px] font-medium uppercase tracking-wide opacity-80" style={{ color: uiColors.chartError }}>
                        Sold
                      </div>
                      <div className="text-lg font-semibold tabular-nums" style={{ color: uiColors.chartError, fontFamily: 'var(--font-mono)' }}>
                        ${formatPremiumMillions(flowTapePremiumSummary.soldPremium)}
                      </div>
                      <div className="text-xs tabular-nums" style={{ color: 'var(--muted)' }}>
                        {flowTapePremiumSummary.pctSold != null
                          ? `${flowTapePremiumSummary.pctSold.toFixed(1)}% of buy+sell premium`
                          : '—'}
                      </div>
                    </div>
                    <div
                      className="min-w-[10rem] flex-1 rounded-lg border px-3 py-2"
                      style={{
                        borderColor: 'var(--hair)',
                        backgroundColor: 'var(--elev2)',
                      }}
                    >
                      <div className="text-[11px] font-medium uppercase tracking-wide opacity-80" style={{ color: uiColors.textMuted }}>
                        Premium delta (buy − sell)
                      </div>
                      <div
                        className="text-lg font-semibold tabular-nums"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          color:
                            flowTapePremiumSummary.premiumDelta > 0
                              ? uiColors.chartSuccess
                              : flowTapePremiumSummary.premiumDelta < 0
                                ? uiColors.chartError
                                : uiColors.text,
                        }}
                      >
                        {flowTapePremiumSummary.premiumDelta >= 0 ? '+' : ''}$
                        {formatPremiumMillions(flowTapePremiumSummary.premiumDelta)}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--muted)' }}>
                        Net from classified sides
                      </div>
                    </div>
                  </div>
                  {flowTapePremiumSummary.midPremium > 0 && (
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      Unclassified / mid tape: ${formatPremiumMillions(flowTapePremiumSummary.midPremium)} (excluded from % and delta above)
                    </p>
                  )}
                </div>
              )}
              {flowTradesLoading && <p className="text-sm opacity-80">Loading flow…</p>}
              {flowTradesError && !flowTrades.length && <p className="text-sm" style={{ color: 'var(--warn)' }}>{flowTradesError}</p>}
              {!flowTradesLoading && flowTrades.length === 0 && !flowTradesError && (
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  No trades for this contract in the selected range.
                </p>
              )}
              {!flowTradesLoading && flowTrades.length > 0 && flowTradesForDisplay.length === 0 && (
                <p className="text-sm" style={{ color: 'var(--warn)' }}>
                  No prints meet this minimum premium. Lower or clear the minimum to see all loaded rows.
                </p>
              )}
              {!flowTradesLoading && flowTradesForDisplay.length > 0 && (
                <>
                  <div
                    className="overflow-x-auto max-h-[min(60vh,28rem)] min-h-[12rem] overflow-y-auto rounded-xl border"
                    style={{ borderColor: 'var(--hair)', backgroundColor: 'var(--elev)' }}
                  >
                    <table className="w-full border-collapse" style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--elev)' }}>
                        <tr style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 600, letterSpacing: '.03em' }}>
                          <th className="text-left p-2 border-b">Date</th>
                          <th className="text-left p-2 border-b">Time</th>
                          <th className="text-right p-2 border-b">Price</th>
                          <th className="text-right p-2 border-b">Size</th>
                          <th className="text-left p-2 border-b">Side</th>
                          <th className="text-left p-2 border-b max-w-[220px]">Condition</th>
                          <th className="text-left p-2 border-b max-w-[260px]">Tape detail</th>
                          <th className="text-right p-2 border-b">Premium</th>
                          <th className="text-left p-2 border-b">Patterns</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flowTablePagination.pageRows.map((t, i) => {
                          const globalIx = flowTablePagination.start + i;
                          return (
                            <tr key={t.id ?? globalIx} style={{ borderBottom: '1px solid var(--hair)' }}>
                              <td className="p-2 whitespace-nowrap">{flowTapeRowDate(t)}</td>
                              <td className="p-2 whitespace-nowrap">{t.time ?? '—'}</td>
                              <td className="p-2 text-right">{typeof t.price === 'number' ? t.price.toFixed(2) : t.price ?? '—'}</td>
                              <td className="p-2 text-right">{(t.volume ?? 0).toLocaleString()}</td>
                              <td className="p-2">
                                {(() => {
                                  const s = String(t.execution_side ?? t.side ?? '').toLowerCase();
                                  if (s === 'buy') {
                                    return <span className="px-2 py-0.5 rounded text-xs" style={{ background: 'var(--up-dim)', color: 'var(--up)' }}>buy</span>;
                                  }
                                  if (s === 'sell') {
                                    return <span className="px-2 py-0.5 rounded text-xs" style={{ background: 'var(--down-dim)', color: 'var(--down)' }}>sell</span>;
                                  }
                                  return <span className="text-xs opacity-70" title="Tape side not classified (mid / unknown)">—</span>;
                                })()}
                              </td>
                              <td
                                className="p-2 text-xs align-top max-w-[220px]"
                                title={t.condition_description || undefined}
                              >
                                <div className="font-mono leading-snug break-words">
                                  {t.condition_label
                                    ? t.condition_label
                                    : (t.condition_codes?.length
                                        ? t.condition_codes.join(', ')
                                        : (t.condition != null && Number.isFinite(t.condition)
                                            ? String(t.condition)
                                            : '—'))}
                                </div>
                                {t.condition_description ? (
                                  <div className="text-[11px] opacity-80 mt-0.5 line-clamp-2">{t.condition_description}</div>
                                ) : null}
                              </td>
                              <td
                                className="p-2 text-xs align-top max-w-[260px]"
                                title={
                                  [
                                    t.trade_payload_detail,
                                    t.theta_payload_json ||
                                      (t.theta_tick != null ? JSON.stringify(t.theta_tick) : '') ||
                                      (t.polygon_tick != null ? JSON.stringify(t.polygon_tick) : ''),
                                  ]
                                    .filter(Boolean)
                                    .join('\n\n—— raw ——\n\n') || undefined
                                }
                              >
                                {t.trade_payload_detail ? (
                                  <div className="font-mono leading-snug break-words text-[11px]">{t.trade_payload_detail}</div>
                                ) : (
                                  (() => {
                                    const raw =
                                      t.theta_payload_json ||
                                      (t.theta_tick != null ? JSON.stringify(t.theta_tick) : '') ||
                                      (t.polygon_tick != null ? JSON.stringify(t.polygon_tick) : '');
                                    if (!raw) return <span className="opacity-70">—</span>;
                                    return (
                                      <span className="font-mono text-[11px] opacity-80">
                                        {raw.length > 120 ? `${raw.slice(0, 120)}…` : raw}
                                      </span>
                                    );
                                  })()
                                )}
                              </td>
                              <td className="p-2 text-right">{(t.premium != null ? Number(t.premium).toLocaleString(undefined, { maximumFractionDigits: 0 }) : t.netFlow ?? '—')}</td>
                              <td className="p-2">
                                {(() => {
                                  // The exchange states whether this print was one leg of a package,
                                  // so the tape can say so outright instead of leaving it to be inferred.
                                  const ml = multiLegInfo(t);
                                  return (
                                    <div className="flex flex-col items-start gap-1">
                                      {ml ? (
                                        <span
                                          className="px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap"
                                          style={{
                                            background:
                                              ml.kind === 'with-stock' ? '#c98bdb22' : '#7c9cff22',
                                            color: ml.kind === 'with-stock' ? '#c98bdb' : '#7c9cff',
                                          }}
                                          title={`${ml.label} (OPRA condition ${ml.code}) — the exchange reported this print as part of a multi-leg trade.`}
                                        >
                                          ⛓ {ml.kind === 'with-stock' ? 'multi-leg +stock' : 'multi-leg'}
                                        </span>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="px-2 py-1 rounded text-xs font-medium disabled:opacity-40"
                                        style={
                                          ml
                                            ? { background: uiColors.chartWarning, color: '#ffffff' }
                                            : {
                                                background: 'transparent',
                                                color: uiColors.textMuted,
                                                border: `1px solid ${uiColors.surfaceBorder}`,
                                              }
                                        }
                                        disabled={!flowPatternContractCtx}
                                        title={
                                          ml
                                            ? 'Find the other legs executed with this print'
                                            : 'The exchange did not flag this as multi-leg; search anyway for prints around it'
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!flowPatternContractCtx) return;
                                          const leg = normalizeHistoricalTradeRow(t, flowPatternContractCtx, globalIx);
                                          if (!leg) return;
                                          setFlowPatternAnchorLeg(leg);
                                          setFlowPatternPanelOpen(true);
                                        }}
                                      >
                                        Find legs
                                      </button>
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {flowTablePagination.totalPages > 1 && (
                    <div className="flex items-center justify-between gap-2 mt-2 text-xs" style={{ color: uiColors.text }}>
                      <button
                        type="button"
                        disabled={flowTablePagination.clampedPage <= 1}
                        onClick={() => setFlowTablePage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1 rounded border disabled:opacity-40"
                        style={{ borderColor: uiColors.surfaceBorder, backgroundColor: uiColors.surfaceHover }}
                      >
                        ← Prev
                      </button>
                      <span>Page {flowTablePagination.clampedPage} of {flowTablePagination.totalPages} ({flowTradesForDisplay.length.toLocaleString()} rows)</span>
                      <button
                        type="button"
                        disabled={flowTablePagination.clampedPage >= flowTablePagination.totalPages}
                        onClick={() => setFlowTablePage((p) => Math.min(flowTablePagination.totalPages, p + 1))}
                        className="px-3 py-1 rounded border disabled:opacity-40"
                        style={{ borderColor: uiColors.surfaceBorder, backgroundColor: uiColors.surfaceHover }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'eod' && loading && <p className="text-sm opacity-80">Loading...</p>}
          {activeTab === 'eod' && error && <p className="text-sm text-red-400">{error}</p>}
          {activeTab === 'eod' && !loading && !error && data.length === 0 && (
            <p className="text-sm opacity-80">No historical data for this contract.</p>
          )}

          {activeTab === 'eod' && !loading && !error && data.length > 0 && (
            <>
              {/* Date range: calendar From / To (like flight search); change then Update to refetch */}
              <div className="flex flex-wrap items-center gap-3 text-sm" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                <label className="opacity-80">From</label>
                <input
                  type="date"
                  aria-label="Start date"
                  min={minDate}
                  max={dateRange.to || today}
                  value={dateRange.from}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
                  className="border rounded-lg px-3 py-2 min-w-[140px]"
                  style={{
                    backgroundColor: uiColors.surface,
                    borderColor: uiColors.surfaceBorder,
                    color: uiColors.text,
                  }}
                />
                <label className="opacity-80">To</label>
                <input
                  type="date"
                  aria-label="End date"
                  min={dateRange.from}
                  max={today}
                  value={dateRange.to}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
                  className="border rounded-lg px-3 py-2 min-w-[140px]"
                  style={{
                    backgroundColor: uiColors.surface,
                    borderColor: uiColors.surfaceBorder,
                    color: uiColors.text,
                  }}
                />
                <button
                  type="button"
                  onClick={handleUpdateDates}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:pointer-events-none transition-colors"
                  style={{ backgroundColor: uiColors.primary, color: uiColors.textInverse }}
                >
                  {loading ? 'Updating…' : 'Update'}
                </button>
              </div>

              {filteredByDate.length === 0 ? (
                <p className="text-sm opacity-80">No data in selected date range. Adjust From/To to see charts.</p>
              ) : (
            <>
              {/* Top row: primary (context-aware) + trades side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {smallChart(primaryLabel, primaryChartData, uiColors, uiColors.chartPrimary, 240, isDarkMode)}
                {smallChart('Premium flow (volume × 100 × mid)', tradesChartData, uiColors, uiColors.chartSuccess, 240, isDarkMode)}
              </div>

              {/* Volume & OI */}
              {smallChart('Volume', filteredByDate.map((r) => ({ date: r.date, value: r.volume })), uiColors, uiColors.chartPrimary, 180, isDarkMode)}
              {smallChart('Open interest', filteredByDate.map((r) => ({ date: r.date, value: r.open_interest })), uiColors, uiColors.chartWarning, 180, isDarkMode)}

              {/* First-order Greeks: Delta, Gamma, Spot Gamma (from EOD) */}
              <h4 className="text-sm font-semibold mt-4 mb-2 opacity-90">First-order Greeks</h4>
              {deltaChartData.length > 0 && smallChart('Delta', deltaChartData, uiColors, uiColors.info, 180, isDarkMode)}
              {gammaChartData.length > 0 && smallChart('Gamma', gammaChartData, uiColors, uiColors.chartError, 180, isDarkMode)}
              {spotGammaChartData.length > 0 && smallChart('Spot Gamma', spotGammaChartData, uiColors, uiColors.chartSecondary, 180, isDarkMode)}

              {/* Historical IV change for the contract */}
              {volatilityChartData.length > 0 && smallChart('Historical IV change (%)', volatilityChartData, uiColors, uiColors.accent, 180, isDarkMode)}

              {/* Second-order Greeks (from Theta when available): vanna, charm, vomma, veta */}
              <h4 className="text-sm font-semibold mt-4 mb-2 opacity-90">Second-order Greeks</h4>
              {(loadingThetaGreeks && thetaSecondOrder.length === 0) && (
                <p className="text-sm opacity-80 mb-2">Loading second/third order Greeks…</p>
              )}
              {thetaSecondOrder.length > 0 && (
                <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                  <h4 className="text-sm font-semibold mb-3 opacity-90">Second-order Greeks (EOD)</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={thetaSecondOrder} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                      <CartesianGrid stroke={gridStroke} vertical={false} />
                      <XAxis dataKey="date" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} />
                      <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={48} />
                      <Tooltip contentStyle={rechartsTooltipContent} labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem} />
                      <Legend wrapperStyle={rechartsLegendStyle} />
                      <Line type="monotone" dataKey="vanna" name="Vanna" stroke={uiColors.info} strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="charm" name="Charm" stroke={uiColors.accent} strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="vomma" name="Vomma" stroke={uiColors.chartWarning} strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="veta" name="Veta" stroke={uiColors.chartSuccess} strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="implied_vol" name="IV (hist)" stroke={uiColors.chartPrimary} strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {thetaThirdOrder.length > 0 && (
                <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                  <h4 className="text-sm font-semibold mb-3 opacity-90">Third-order Greeks (EOD)</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={thetaThirdOrder} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                      <CartesianGrid stroke={gridStroke} vertical={false} />
                      <XAxis dataKey="date" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} />
                      <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={48} />
                      <Tooltip contentStyle={rechartsTooltipContent} labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem} />
                      <Legend wrapperStyle={rechartsLegendStyle} />
                      <Line type="monotone" dataKey="speed" name="Speed" stroke={uiColors.chartError} strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="color" name="Color" stroke={uiColors.secondary} strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="ultima" name="Ultima" stroke={uiColors.chartWarning} strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {!loadingThetaGreeks && thetaSecondOrder.length === 0 && thetaThirdOrder.length === 0 && isThetaDataAvailable() && (
                <p className="text-xs opacity-75 mb-4">
                  Higher-order sensitivity series are unavailable for this contract or date range, or the response could not be read. First-order Greeks above still reflect the daily series in this view.
                </p>
              )}

              {/* Bought vs sold % on this contract (from bid/ask size as proxy) */}
              {boughtSoldPctData.some((d) => d.total > 0) && (
                <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                  <h4 className="text-sm font-semibold mb-3 opacity-90">Bought vs sold % (contract)</h4>
                  <p className="text-xs opacity-75 mb-2">End-of-day quote book: ask size is treated as liftable (buy) interest and bid size as hit (sell) interest. This is a depth snapshot, not tick-classified prints.</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={boughtSoldPctData} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                      <CartesianGrid stroke={gridStroke} vertical={false} />
                      <XAxis dataKey="date" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} />
                      <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={48} domain={[0, 100]} unit="%" />
                      <Tooltip
                        contentStyle={rechartsTooltipContent}
                        labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                        formatter={(v: number) => [v.toFixed(1) + '%', '']}
                      />
                      <Legend wrapperStyle={rechartsLegendStyle} />
                      <Line type="monotone" dataKey="boughtPct" name="Bought %" stroke={uiColors.chartSuccess} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="soldPct" name="Sold %" stroke={uiColors.chartError} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Per-session open vs close, from the change in open interest between EOD bars.
                  Costs nothing: open interest and volume are already on every EOD row, so this
                  is arithmetic over data this modal has fetched anyway. */}
              {filteredByDate.length > 1 && (() => {
                const effects = eodPositionEffects(filteredByDate as EodOiRow[], {
                  // Settled by the figure OPRA published this morning, which reports the
                  // close of the session before it. Labelled with the stamp the endpoint
                  // returned rather than with the local clock, so a figure that settles a
                  // different session than expected is visible instead of misattributed.
                  publishedOi: publishedOi?.openInterest,
                  today: publishedOi?.publishedOn,
                });
                const recent = filteredByDate.slice(-12).reverse();
                return (
                  <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                    <h4 className="text-sm font-semibold mb-1 opacity-90">Opening or closing, by session</h4>
                    <p className="text-xs opacity-75 mb-2">
                      Open interest rising means contracts were created; falling means they were
                      retired. Volume alone cannot tell the two apart — the same 200 contracts
                      opened and shut twice trade 400. &ldquo;Left&rdquo; is how much of the session&rsquo;s
                      volume actually became a position rather than being closed out the same day.
                    </p>
                    <p className="text-xs opacity-60 mb-3">
                      Open interest is published once, the morning after the session it reports,
                      so a session is settled by the <em>next</em> morning&rsquo;s figure. Yesterday
                      is therefore answerable today. Only a session whose figure has not been
                      published yet reads Pending — normally just today&rsquo;s.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        <thead>
                          <tr style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 600, letterSpacing: '.03em' }}>
                            <th className="text-left p-2 border-b">Date</th>
                            <th className="text-left p-2 border-b">Effect</th>
                            <th className="text-right p-2 border-b">ΔOI</th>
                            <th className="text-right p-2 border-b">Open interest</th>
                            <th className="text-right p-2 border-b">Volume</th>
                            <th className="text-right p-2 border-b">Left</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recent.map((r) => {
                            const e = effects.get(r.date);
                            const colour =
                              e?.label === 'Opening'
                                ? uiColors.chartSuccess
                                : e?.label === 'Closing'
                                  ? uiColors.chartError
                                  : uiColors.textMuted;
                            return (
                              <tr key={r.date}>
                                <td className="p-2 border-b whitespace-nowrap">{r.date}</td>
                                <td
                                  className="p-2 border-b"
                                  style={{ color: e?.pending ? uiColors.textMuted : colour, fontWeight: 600 }}
                                  title={
                                    e?.pending
                                      ? 'Open interest for this session is published next morning'
                                      : e?.confirmedBy
                                        ? `Confirmed by the open-interest figure dated ${e.confirmedBy}`
                                        : undefined
                                  }
                                >
                                  {formatPositionEffect(e).split(' ')[0]}
                                </td>
                                <td className="p-2 border-b text-right" style={{ color: colour }}>
                                  {e?.change == null ? '—' : `${e.change > 0 ? '+' : ''}${e.change.toLocaleString()}`}
                                </td>
                                <td className="p-2 border-b text-right">{(r.open_interest ?? 0).toLocaleString()}</td>
                                <td className="p-2 border-b text-right">{(r.volume ?? 0).toLocaleString()}</td>
                                <td className="p-2 border-b text-right opacity-75">
                                  {e?.conviction == null ? '—' : `${Math.round(e.conviction * 100)}%`}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {(() => {
                      const latest = filteredByDate[filteredByDate.length - 1];
                      const e = effects.get(latest.date);
                      if (!e?.pending) return null;
                      const traced = counterFlowCandidates(
                        filteredByDate as EodOiRow[],
                        effects,
                        Number(latest.volume) || 0,
                      );
                      return (
                        <div className="mt-3 pt-3 border-t text-xs">
                          <span className="font-semibold opacity-90">{latest.date} — what can be said now: </span>
                          {e.provableNew != null ? (
                            <span style={{ color: uiColors.chartSuccess }}>
                              at least {e.provableNew.toLocaleString()} contracts must be new, because volume
                              ({Number(latest.volume).toLocaleString()}) exceeds open interest
                              ({Number(latest.open_interest).toLocaleString()}).
                            </span>
                          ) : (
                            <span className="opacity-75">
                              volume ({Number(latest.volume ?? 0).toLocaleString()}) is within open interest
                              ({Number(latest.open_interest ?? 0).toLocaleString()}), so it could be either —
                              the next open-interest print settles it.
                            </span>
                          )}
                          {traced.length > 0 && (
                            <span className="opacity-75">
                              {' '}Comparable position{traced.length > 1 ? 's were' : ' was'} opened on{' '}
                              {traced.map((c, i) => (
                                <span key={c.date}>
                                  {i > 0 ? ', ' : ''}
                                  <strong>{c.date}</strong> (+{c.opened.toLocaleString()})
                                </span>
                              ))}
                              {' '}— today&rsquo;s flow may be unwinding {traced.length > 1 ? 'one of those' : 'it'},
                              though matching sizes is suggestive rather than conclusive.
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* Today's session so far.
                  Open interest is published once, after the close, so there is no live OI to
                  difference against yesterday's. The panel this replaces compared today's
                  figure against the row it had itself appended, so it read UNCHANGED whatever
                  the market did. What IS knowable: the last published open interest, today's
                  volume, and the contracts the two together PROVE must be new. */}
              {todaySnapshot && (() => {
                // The row's open-interest column reflects the session BEFORE it, so the label needs
                // the one before that. Verified on SPXW 7785C: 461 traded 08-18, and the resulting
                // 434 lands on the 08-19 row.
                const completedRows = filteredByDate.filter((r) => !(r as { is_today?: boolean }).is_today);
                const lastEod = completedRows[completedRows.length - 1];
                const priorOiAsOf = completedRows[completedRows.length - 2]?.date;
                const reading = liveOiReading({
                  snapshotOi: todaySnapshot.open_interest,
                  snapshotVolume: todaySnapshot.volume,
                  priorOi: lastEod?.open_interest,
                  priorDate: lastEod?.date,
                  priorOiAsOf,
                });
                // Nothing has printed yet — outside RTH there is no session to describe.
                if (reading.state === 'pending' || reading.volume === null) return null;
                const volOi = reading.openInterest ? reading.volume / reading.openInterest : null;
                return (
                  <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                    <h4 className="text-sm font-semibold mb-3 opacity-90">Today's session so far</h4>
                    <p className="text-xs opacity-75 mb-3">
                      Open interest is published once, after the close, so the figure below is the
                      last published one and does not move during the day. Volume is what has
                      traded since the open. Where volume exceeds open interest the excess must be
                      newly created, because closing a contract requires one to exist.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="opacity-60">Open interest</span>
                        <div className="font-semibold">{openInterestLabel(reading)}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Today volume</span>
                        <div className="font-semibold">{reading.volume.toLocaleString()}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Vol / OI</span>
                        <div className="font-semibold">{volOi === null ? '—' : `${volOi.toFixed(2)}×`}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Proven new</span>
                        <div className="font-semibold">
                          {reading.minNewContracts === null
                            ? '—'
                            : `≥ ${reading.minNewContracts.toLocaleString()}`}
                        </div>
                      </div>
                      <div>
                        <span className="opacity-60">Today last</span>
                        <div className="font-semibold">${(todaySnapshot.last || 0).toFixed(2)}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Underlying</span>
                        <div className="font-semibold">${(todaySnapshot.underlying_price || 0).toFixed(2)}</div>
                      </div>
                      <div className="col-span-2">
                        <span className="opacity-60">Next published OI must land</span>
                        <div className="font-semibold">
                          {reading.oiFloor === null || reading.oiCeiling === null
                            ? '—'
                            : `${reading.oiFloor.toLocaleString()} – ${reading.oiCeiling.toLocaleString()}`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Open vs close %: volume that opened vs closed positions (from OI change) */}
              {openClosePctData.some((d) => d.openMinPct !== null) && (
                <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                  <h4 className="text-sm font-semibold mb-3 opacity-90">Opened vs closed, as a range</h4>
                  <p className="text-xs opacity-75 mb-2">The exact split is not recoverable: volume where one side opened against the other closing never touches open interest, so it is invisible in the daily change. The solid edge is the floor — the share that MUST have opened or closed, because open interest cannot move by more than was actually created or retired. The shaded band above it is everything the data leaves undecided, and its top edge is the old estimate, which assumed no trade was ever a transfer between an opener and a closer. Sessions with no prior open interest published, or still in progress, are left blank rather than split.</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={openClosePctData} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                      <CartesianGrid stroke={gridStroke} vertical={false} />
                      <XAxis dataKey="date" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} />
                      <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={48} domain={[0, 100]} />
                      <Tooltip
                        contentStyle={rechartsTooltipContent}
                        labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                        formatter={(v: number) => [v.toFixed(1) + '%', '']}
                      />
                      <Legend wrapperStyle={rechartsLegendStyle} />
                      {/* Base area draws the provable floor as its top edge; the stacked band above it is
                          the range the data does not pin down. `connectNulls` stays off so a session that
                          cannot be split leaves a gap instead of a line drawn through it. */}
                      <Area
                        type="monotone" dataKey="openMinPct" stackId="open" name="Opened — at least"
                        stroke={uiColors.chartPrimary} strokeWidth={2} fill="none" connectNulls={false}
                      />
                      <Area
                        type="monotone" dataKey="openRangePct" stackId="open" name="Opened — possible range"
                        stroke="none" fill={uiColors.chartPrimary} fillOpacity={0.22} connectNulls={false}
                      />
                      <Area
                        type="monotone" dataKey="closeMinPct" stackId="close" name="Closed — at least"
                        stroke={uiColors.chartWarning} strokeWidth={2} fill="none" connectNulls={false}
                      />
                      <Area
                        type="monotone" dataKey="closeRangePct" stackId="close" name="Closed — possible range"
                        stroke="none" fill={uiColors.chartWarning} fillOpacity={0.16} connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Print-classified volume by calendar day (same logic as Intraday tab; up to 21 days per request) */}
              {params?.strike != null && params?.expiry && params.expiry !== 'all' && (
                <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                  <h4 className="text-sm font-semibold mb-2 opacity-90">Print-based volume by day (this contract)</h4>
                  <p className="text-xs opacity-75 mb-2">
                    Stacked contracts: at bid (red), inside spread (gray), at ask (green). Same print-vs-NBBO methodology as the Intraday tab, computed per calendar day for your selected range (up to 30 days per request).
                  </p>
                  {tapeByDayLoading && <p className="text-sm opacity-80">Loading print classification by day…</p>}
                  {!tapeByDayLoading && tapeByDayChartData.length === 0 && (
                    <p className="text-sm opacity-80">No multi-day tape data for this range (or data temporarily unavailable).</p>
                  )}
                  {!tapeByDayLoading && tapeByDayChartData.some((r) => r.atBid + r.atAsk + r.between > 0) && (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={tapeByDayChartData} margin={{ top: 12, right: 12, left: 8, bottom: 28 }}>
                        <CartesianGrid stroke={gridStroke} vertical={false} />
                        <XAxis dataKey="date" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={{ ...axisTick, fontSize: 10 }} angle={-35} textAnchor="end" height={50} />
                        <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={44} />
                        <Tooltip
                          contentStyle={rechartsTooltipContent}
                          labelFormatter={(_, payload) => (payload?.[0]?.payload as { dateFull?: string })?.dateFull ?? ''}
                          formatter={(v: number) => [v.toLocaleString(), 'contracts']}
                        />
                        <Legend wrapperStyle={rechartsLegendStyle} />
                        <Bar dataKey="atBid" stackId="d" name="At bid" fill={uiColors.chartError} />
                        <Bar dataKey="between" stackId="d" name="Inside spread" fill={uiColors.textMuted} />
                        <Bar dataKey="atAsk" stackId="d" name="At ask" fill={uiColors.chartSuccess} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}

              {/* Flow by side: bought/sold calls and puts (bid_size = sell, ask_size = buy) */}
              {flowChartData.length > 0 && (
                <div className="mb-6 rounded-2xl p-4 border" style={historicalChartCardStyle}>
                  <h4 className="text-sm font-semibold mb-3 opacity-90">Flow by side · Strike {params?.strike}{params?.expiry && params.expiry !== 'all' ? ` · Expiry ${params.expiry}` : ''}</h4>
                  <p className="text-xs opacity-75 mb-2">Per-day sum of end-of-day ask size (lift / buy interest) and bid size (hit / sell interest) for calls and puts. When both legs exist for a date, you see four lines; single-leg days show zeros on the missing side.</p>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={flowChartData} margin={{ top: 12, right: 12, left: 8, bottom: 12 }}>
                      <CartesianGrid stroke={gridStroke} vertical={false} />
                      <XAxis dataKey="date" stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} />
                      <YAxis stroke={stroke} axisLine={{ stroke: chartT.border }} tickLine={false} tick={axisTick} width={48} />
                      <Tooltip
                        contentStyle={rechartsTooltipContent}
                        labelStyle={rechartsTooltipLabel} itemStyle={rechartsTooltipItem}
                        formatter={(v: number) => [typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : v]}
                      />
                      <Legend wrapperStyle={rechartsLegendStyle} />
                      <Line type="monotone" dataKey="boughtCall" name="Bought call" stroke={uiColors.boughtCalls} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="soldCall" name="Sold call" stroke={uiColors.soldCalls} strokeDasharray="5 4" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="boughtPut" name="Bought put" stroke={uiColors.boughtPuts} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="soldPut" name="Sold put" stroke={uiColors.soldPuts} strokeDasharray="5 4" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* 6) Unusual flow (relative > 3) */}
              <div className="mb-6">
                <h4 className="text-sm font-medium mb-2 opacity-90">
                  Unusual flow (value / average &gt; {RELATIVE_UNUSUAL_THRESHOLD})
                </h4>
                <p className="text-xs opacity-75 mb-2">
                  Averages: Volume {averages.volume.toFixed(0)} · Bid size {averages.bid_size.toFixed(0)} · Ask size {averages.ask_size.toFixed(0)} · Premium {averages.premium.toFixed(0)} · OI {averages.open_interest.toFixed(0)}
                </p>
                {unusualFlow.length === 0 ? (
                  <p className="text-sm opacity-80">No days above threshold.</p>
                ) : (
                  <div className="overflow-x-auto max-h-48 overflow-y-auto">
                    <table className="w-full border-collapse" style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--elev)' }}>
                        <tr style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 600, letterSpacing: '.03em' }}>
                          <th className="text-left p-2 border-b">Date</th>
                          <th className="text-left p-2 border-b">Metric</th>
                          <th className="text-right p-2 border-b">Value</th>
                          <th className="text-right p-2 border-b">Avg</th>
                          <th className="text-right p-2 border-b">Ratio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unusualFlow.map((u, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--hair)' }}>
                            <td className="p-2">{u.date}</td>
                            <td className="p-2">{u.metric}</td>
                            <td className="p-2 text-right">{u.value.toLocaleString()}</td>
                            <td className="p-2 text-right">{u.avg.toFixed(0)}</td>
                            <td className="p-2 text-right" style={{ color: 'var(--warn)' }}>{u.ratio.toFixed(2)}×</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ContractHistoricalModal;
